import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { SOFTWARE_ROLES, getSoftwareRoleLabel } from '../utils/accessControl';
import './HrModule.css';

const todayValue = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const monthValue = () => todayValue().slice(0, 7);
const yearValue = () => Number(todayValue().slice(0, 4));

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const currency = (value) => `Tk ${Number(value || 0).toFixed(2)}`;
const hoursLabel = (value) => `${Number(value || 0).toFixed(2)}h`;
const percentLabel = (value) => `${Number(value || 0).toFixed(1)}%`;
const SERVICE_ENGINEER_DESIGNATION = 'Service Engineer';
const ADMIN_DEPARTMENT = 'Admin';
const VIP_DESIGNATION = 'VIP';

const emptyLeaveTypeForm = () => ({
  leaveTypeId: '',
  leaveTypeName: '',
  leaveCode: '',
  annualAllocation: '',
  isPaid: true,
  isActive: true,
});

const emptyCalendarSettings = () => ({
  year: yearValue(),
  weekendDays: [],
  holidays: [],
});

const emptyHolidayForm = () => ({
  holidayId: '',
  holidayDate: todayValue(),
  holidayName: '',
  notes: '',
});

const emptyEmployeeForm = () => ({
  employeeCode: '',
  photoData: '',
  appUsername: '',
  appPassword: '',
  softwareRole: SOFTWARE_ROLES.NONE,
  mobileAccessEnabled: false,
  fullName: '',
  phoneNumber: '',
  email: '',
  address: '',
  departmentId: '',
  designationId: '',
  jobGroupIds: [],
  roleId: '',
  joiningDate: todayValue(),
  basicSalary: '',
  allowance: '',
  deduction: '',
  overtimeRate: '',
  employmentStatus: 'Active',
  notes: '',
});

const emptyJobGroupForm = () => ({
  groupCode: '',
  groupName: '',
  selectedEmployeeId: '',
  members: [],
});

const emptyWorkshopBoard = () => ({
  summary: {},
  leaderboard: [],
  employees: [],
  settings: {},
});

const emptyPayrollPaymentDialog = () => ({
  open: false,
  loading: false,
  run: null,
  employees: [],
  payments: [],
  accounts: [],
  paymentDate: todayValue(),
  paymentAccountCode: '11101',
  notes: '',
  entries: [],
});

const emptyProductivitySettings = () => ({
  LeaderboardWeightEfficiency: 40,
  LeaderboardWeightRevenue: 25,
  LeaderboardWeightHours: 20,
  LeaderboardWeightClosedJobs: 15,
  TargetEfficiencyPercent: 100,
  TargetBillableHoursPerDay: 6,
  OverloadHoursThreshold: 8,
  HighTaskCountThreshold: 5,
  LowTaskCountThreshold: 2,
  ShiftStartTime: '09:00',
  ShiftEndTime: '18:00',
});

const HR_REPORT_CATALOG = [
  {
    id: 'employees',
    eyebrow: 'People',
    title: 'Employee List Report',
    description: 'Directory, department, designation, and salary view for your full workshop team.',
  },
  {
    id: 'attendance',
    eyebrow: 'Time',
    title: 'Attendance Report',
    description: 'Monthly attendance log with late minutes, overtime minutes, and daily status.',
  },
  {
    id: 'leaves',
    eyebrow: 'Leave',
    title: 'Leave Report',
    description: 'Requests, approval status, leave types, and requested days in one place.',
  },
  {
    id: 'payroll',
    eyebrow: 'Payroll',
    title: 'Payroll / Salary Sheet',
    description: 'Salary preview with basic pay, allowance, deduction, overtime, and net pay.',
  },
];

const formatHumanDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatHumanDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const monthLabel = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return value || '';
  }

  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

function HrModule({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const routeRefreshToken = location.state?.refreshAt || location.key;
  const loadEverythingRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [access, setAccess] = useState(null);
  const [lookups, setLookups] = useState({
    departments: [],
    designations: [],
    jobGroups: [],
    leaveTypes: [],
    workshopRoles: [],
    softwareRoles: [],
  });
  const [dashboard, setDashboard] = useState({
    summary: {},
    attendanceToday: {},
    leaveToday: {},
    topPerformers: [],
    latestPayroll: null,
    currentMonth: monthValue(),
  });
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm());
  const [jobGroups, setJobGroups] = useState([]);
  const [editingJobGroupId, setEditingJobGroupId] = useState(null);
  const [jobGroupForm, setJobGroupForm] = useState(emptyJobGroupForm());
  const [attendanceMonth, setAttendanceMonth] = useState(monthValue());
  const [attendanceEmployeeId, setAttendanceEmployeeId] = useState('');
  const [attendanceEmployeeCode, setAttendanceEmployeeCode] = useState('');
  const [attendanceSearchEmployeeCode, setAttendanceSearchEmployeeCode] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({});
  const [attendanceForm, setAttendanceForm] = useState({
    employeeId: '',
    attendanceDate: todayValue(),
    checkInTime: '09:00',
    checkOutTime: '18:00',
    attendanceStatus: 'Present',
    notes: '',
  });
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveSearchEmployeeId, setLeaveSearchEmployeeId] = useState('');
  const [leaveBalanceEmployeeId, setLeaveBalanceEmployeeId] = useState('');
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveForm, setLeaveForm] = useState({
    employeeId: '',
    leaveTypeId: '',
    startDate: todayValue(),
    endDate: todayValue(),
    reason: '',
  });
  const [payrollMonth, setPayrollMonth] = useState(monthValue());
  const [payrollPreview, setPayrollPreview] = useState({ lines: [], summary: {} });
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payrollAdjustments, setPayrollAdjustments] = useState([]);
  const [payrollPaymentDialog, setPayrollPaymentDialog] = useState(emptyPayrollPaymentDialog());
  const [adjustmentForm, setAdjustmentForm] = useState({
    employeeId: '',
    payrollMonth: monthValue(),
    adjustmentType: 'Allowance',
    amount: '',
    notes: '',
  });

  const printPayrollSheet = () => {
    const monthLabelText = monthLabel(payrollMonth);
    const rowsHtml = payrollPreview.lines?.length > 0
      ? payrollPreview.lines.map((line) => `
          <tr>
            <td>${line.FullName || ''}</td>
            <td>${currency(line.BasicSalary)}</td>
            <td>${currency(line.AllowanceAmount)}</td>
            <td>${currency(line.DeductionAmount)}</td>
            <td>${currency(line.OvertimeAmount)} (${line.OvertimeHours || 0}h)</td>
            <td>${currency(line.NetSalary)}</td>
          </tr>
        `).join('')
      : `
          <tr>
            <td colspan="6" style="text-align:center;padding:12px;">No active employees available for payroll.</td>
          </tr>
        `;

    const html = `
      <html>
        <head>
          <title>Payroll Sheet - ${monthLabelText}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
            h1 { font-size: 24px; margin-bottom: 0.2em; }
            p { margin: 0.2em 0 1em; }
            table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
            th, td { border: 1px solid #444; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
            .summary { margin-top: 1.5rem; width: auto; }
            .summary td { border: none; padding: 4px 8px; }
            .summary strong { font-weight: 600; }
          </style>
        </head>
        <body>
          <h1>Payroll Sheet</h1>
          <p><strong>Month:</strong> ${monthLabelText}</p>
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Basic</th>
                <th>Allowance</th>
                <th>Deduction</th>
                <th>OT</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <table class="summary">
            <tr><td><strong>Total Employees</strong></td><td>${payrollPreview.summary.employeeCount || 0}</td></tr>
            <tr><td><strong>Total Net Payout</strong></td><td>${currency(payrollPreview.summary.totalNet || 0)}</td></tr>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const [productivity, setProductivity] = useState([]);
  const [workshopMonth, setWorkshopMonth] = useState(monthValue());
  const [workshopBoard, setWorkshopBoard] = useState(emptyWorkshopBoard());
  const [queueSearch, setQueueSearch] = useState('');
  const [queueFilter, setQueueFilter] = useState('active');
  const [queueSort, setQueueSort] = useState('priority');
  const [queuePageSize, setQueuePageSize] = useState(25);
  const [queuePage, setQueuePage] = useState(1);
  const [expandedQueueEmployees, setExpandedQueueEmployees] = useState({});
  const [productivitySettingsForm, setProductivitySettingsForm] = useState(emptyProductivitySettings());
  const [roleMappings, setRoleMappings] = useState([]);
  const [roleForm, setRoleForm] = useState({
    userEmail: '',
    appRoleName: 'Employee',
    employeeId: '',
  });
  const [settingsYear, setSettingsYear] = useState(String(yearValue()));
  const [leaveTypeSettings, setLeaveTypeSettings] = useState([]);
  const [leaveTypeForm, setLeaveTypeForm] = useState(emptyLeaveTypeForm());
  const [calendarSettings, setCalendarSettings] = useState(emptyCalendarSettings());
  const [holidayForm, setHolidayForm] = useState(emptyHolidayForm());
  const [reportMonth, setReportMonth] = useState(monthValue());
  const [reportLoading, setReportLoading] = useState(false);
  const [reportWorkspace, setReportWorkspace] = useState({
    reportType: '',
    rows: [],
    summary: {},
    loadedAt: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const authConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  }), []);

  const visibleDesignations = useMemo(() => (
    lookups.designations.filter((designation) => (
      !employeeForm.departmentId
      || String(designation.DepartmentId || '') === String(employeeForm.departmentId)
    ))
  ), [employeeForm.departmentId, lookups.designations]);

  const selectedDesignation = useMemo(() => (
    lookups.designations.find((designation) => String(designation.DesignationId) === String(employeeForm.designationId)) || null
  ), [employeeForm.designationId, lookups.designations]);

  const requiresJobGroup = selectedDesignation?.DesignationName === SERVICE_ENGINEER_DESIGNATION;

  const employeeOptions = useMemo(() => (
    employees.filter((employee) => employee.EmploymentStatus === 'Active' || employee.IsActive)
  ), [employees]);
  const availableJobGroupEmployees = useMemo(() => (
    employeeOptions.filter((employee) => !jobGroupForm.members.some((member) => (
      String(member.EmployeeId) === String(employee.EmployeeId)
    )))
  ), [employeeOptions, jobGroupForm.members]);

  const holidayMap = useMemo(() => (
    new Map((calendarSettings.holidays || []).map((holiday) => [holiday.HolidayDate, holiday]))
  ), [calendarSettings.holidays]);

  const calendarMonths = useMemo(() => (
    Array.from({ length: 12 }, (_, monthIndex) => {
      const firstDate = new Date(calendarSettings.year, monthIndex, 1);
      const leadingBlanks = firstDate.getDay();
      const daysInMonth = new Date(calendarSettings.year, monthIndex + 1, 0).getDate();
      const cells = [];

      for (let blankIndex = 0; blankIndex < leadingBlanks; blankIndex += 1) {
        cells.push({ key: `blank-${monthIndex}-${blankIndex}`, isBlank: true });
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(calendarSettings.year, monthIndex, day);
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        const dateKey = date.toISOString().slice(0, 10);
        cells.push({
          key: dateKey,
          isBlank: false,
          dateKey,
          day,
          weekday: date.getDay(),
          holiday: holidayMap.get(dateKey) || null,
        });
      }

      return {
        monthIndex,
        label: MONTH_LABELS[monthIndex],
        cells,
      };
    })
  ), [calendarSettings.year, holidayMap]);

  const payrollPaymentTotals = useMemo(() => {
    const totalDue = payrollPaymentDialog.entries.reduce((sum, entry) => sum + Number(entry.dueAmount || 0), 0);
    const totalPaying = payrollPaymentDialog.entries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    return {
      totalDue: Number(totalDue.toFixed(2)),
      totalPaying: Number(totalPaying.toFixed(2)),
      remaining: Number(Math.max(0, totalDue - totalPaying).toFixed(2)),
    };
  }, [payrollPaymentDialog.entries]);

  const selectedBalanceEmployeeId = leaveBalanceEmployeeId || leaveForm.employeeId || access?.employeeId || employeeOptions[0]?.EmployeeId || '';
  const activeReportMeta = useMemo(() => (
    HR_REPORT_CATALOG.find((report) => report.id === reportWorkspace.reportType) || null
  ), [reportWorkspace.reportType]);
  const queueEmployees = useMemo(() => {
    const normalizedSearch = queueSearch.trim().toLowerCase();
    const items = [...(workshopBoard.employees || [])].filter((employee) => {
      const openTaskCount = Number(employee.OpenTaskCount || 0);
      const activeTaskCount = Number(employee.ActiveTaskCount || 0);
      const isRunning = activeTaskCount > 0;
      const isIdle = activeTaskCount === 0 && openTaskCount === 0;
      const isOverloaded = String(employee.WorkloadStatus || '').toLowerCase() === 'overloaded';

      const matchesFilter = (
        queueFilter === 'all'
        || (queueFilter === 'active' && (isRunning || openTaskCount > 0))
        || (queueFilter === 'running' && isRunning)
        || (queueFilter === 'open' && openTaskCount > 0)
        || (queueFilter === 'idle' && isIdle)
        || (queueFilter === 'overloaded' && isOverloaded)
      );

      if (!matchesFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const queueSearchText = [
        employee.FullName,
        employee.DesignationName,
        employee.RoleName,
        employee.DepartmentName,
        employee.WorkloadStatus,
        ...(employee.QueueItems || []).flatMap((task) => ([
          task.JobNumber,
          task.JobStatus,
          task.JobTitle,
          task.ClientName,
          task.VehicleLabel,
          task.LaborName,
          task.LaborDescription,
        ])),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return queueSearchText.includes(normalizedSearch);
    });

    items.sort((left, right) => {
      const leftIsRunning = Number(left.ActiveTaskCount || 0) > 0;
      const rightIsRunning = Number(right.ActiveTaskCount || 0) > 0;

      if (queueSort === 'name') {
        return left.FullName.localeCompare(right.FullName);
      }

      if (queueSort === 'tasks') {
        return (
          Number(right.OpenTaskCount || 0) - Number(left.OpenTaskCount || 0)
          || Number(right.ActiveTaskCount || 0) - Number(left.ActiveTaskCount || 0)
          || Number(right.RemainingHours || 0) - Number(left.RemainingHours || 0)
          || left.FullName.localeCompare(right.FullName)
        );
      }

      if (queueSort === 'remaining') {
        return (
          Number(right.RemainingHours || 0) - Number(left.RemainingHours || 0)
          || Number(right.OpenTaskCount || 0) - Number(left.OpenTaskCount || 0)
          || Number(right.ActiveTaskCount || 0) - Number(left.ActiveTaskCount || 0)
          || left.FullName.localeCompare(right.FullName)
        );
      }

      if (queueSort === 'score') {
        return (
          Number(right.ProductivityScorePercent || 0) - Number(left.ProductivityScorePercent || 0)
          || Number(right.EfficiencyPercent || 0) - Number(left.EfficiencyPercent || 0)
          || Number(right.OpenTaskCount || 0) - Number(left.OpenTaskCount || 0)
          || left.FullName.localeCompare(right.FullName)
        );
      }

      return (
        Number(rightIsRunning) - Number(leftIsRunning)
        || Number(right.OpenTaskCount || 0) - Number(left.OpenTaskCount || 0)
        || Number(right.RemainingHours || 0) - Number(left.RemainingHours || 0)
        || Number(right.ProductivityScorePercent || 0) - Number(left.ProductivityScorePercent || 0)
        || left.FullName.localeCompare(right.FullName)
      );
    });

    return items;
  }, [queueFilter, queueSearch, queueSort, workshopBoard.employees]);
  const queuePagination = useMemo(() => {
    const total = queueEmployees.length;
    const pageSize = Math.max(1, Number(queuePageSize || 25));
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(queuePage, pageCount);
    const startIndex = total === 0 ? 0 : (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);

    return {
      total,
      pageSize,
      pageCount,
      currentPage,
      startIndex,
      endIndex,
    };
  }, [queueEmployees.length, queuePage, queuePageSize]);
  const visibleQueueEmployees = useMemo(() => (
    queueEmployees.slice(queuePagination.startIndex, queuePagination.endIndex)
  ), [queueEmployees, queuePagination.endIndex, queuePagination.startIndex]);

  const clearNotice = useCallback(() => {
    window.setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3500);
  }, []);

  const showError = useCallback((message) => {
    setError(message);
    setSuccess('');
    clearNotice();
  }, [clearNotice]);

  const showSuccess = useCallback((message) => {
    setSuccess(message);
    setError('');
    clearNotice();
  }, [clearNotice]);

  const loadAccessAndLookups = useCallback(async () => {
    const [accessRes, lookupsRes] = await Promise.all([
      axios.get('/api/hr/access', authConfig),
      axios.get('/api/hr/lookups', authConfig),
    ]);

    const resolvedAccess = accessRes.data.access || null;
    setAccess(resolvedAccess);
    setLookups(lookupsRes.data.lookups || {
      departments: [],
      designations: [],
      jobGroups: [],
      leaveTypes: [],
      workshopRoles: [],
      softwareRoles: [],
    });

    return resolvedAccess;
  }, [authConfig]);

  const loadEmployees = useCallback(async (search = employeeSearch) => {
    const response = await axios.get('/api/hr/employees', {
      ...authConfig,
      params: {
        search,
      },
    });
    setEmployees(response.data.employees || []);
    return response.data.employees || [];
  }, [authConfig, employeeSearch]);

  const loadJobGroups = useCallback(async () => {
    const response = await axios.get('/api/hr/job-groups', authConfig);
    setJobGroups(response.data.jobGroups || []);
    return response.data.jobGroups || [];
  }, [authConfig]);

  const loadDashboard = useCallback(async (month = payrollMonth) => {
    const response = await axios.get('/api/hr/dashboard', {
      ...authConfig,
      params: { month },
    });
    setDashboard(response.data.dashboard || {
      summary: {},
      attendanceToday: {},
      leaveToday: {},
      topPerformers: [],
      latestPayroll: null,
      currentMonth: month,
    });
  }, [authConfig, payrollMonth]);

  const loadAttendance = useCallback(async (month = attendanceMonth, employeeId = attendanceEmployeeId, employeeCode = attendanceEmployeeCode) => {
    const response = await axios.get('/api/hr/attendance/report', {
      ...authConfig,
      params: {
        month,
        employeeId: employeeId || undefined,
        employeeCode: employeeId ? undefined : employeeCode || undefined,
      },
    });
    setAttendance(response.data.attendance || []);
    setAttendanceSummary(response.data.summary || {});
  }, [attendanceEmployeeCode, attendanceEmployeeId, attendanceMonth, authConfig]);

  useEffect(() => {
    if (activeTab === 'attendance') {
      loadAttendance(attendanceMonth, attendanceEmployeeId, attendanceEmployeeCode).catch((loadError) => {
        showError(loadError.response?.data?.message || 'Failed to refresh attendance report.');
      });
    }
  }, [activeTab, attendanceEmployeeCode, attendanceMonth, attendanceEmployeeId, loadAttendance, showError]);

  const loadLeaves = useCallback(async (employeeId = selectedBalanceEmployeeId) => {
    const year = Number((leaveForm.startDate || todayValue()).slice(0, 4));
    const requestParams = {};
    if (access?.role === 'Employee') {
      requestParams.employeeId = access.employeeId;
    }

    const [requestsRes, balancesRes] = await Promise.all([
      axios.get('/api/hr/leave/requests', { ...authConfig, params: requestParams }),
      employeeId
        ? axios.get('/api/hr/leave/balances', {
          ...authConfig,
          params: { employeeId, year },
        })
        : Promise.resolve({ data: { balances: [] } }),
    ]);

    setLeaveRequests(requestsRes.data.requests || []);
    setLeaveBalances(balancesRes.data.balances || []);
  }, [access, authConfig, leaveForm.startDate, selectedBalanceEmployeeId]);

  const loadPayroll = useCallback(async (month = payrollMonth) => {
    const [previewRes, runsRes, adjustmentsRes, productivityRes] = await Promise.all([
      axios.get('/api/hr/payroll/preview', { ...authConfig, params: { month } }),
      axios.get('/api/hr/payroll/runs', authConfig),
      axios.get('/api/hr/payroll/adjustments', { ...authConfig, params: { month } }),
      axios.get('/api/hr/integration/productivity', { ...authConfig, params: { month } }),
    ]);

    setPayrollPreview(previewRes.data.preview || { lines: [], summary: {} });
    setPayrollRuns(runsRes.data.runs || []);
    setPayrollAdjustments(adjustmentsRes.data.adjustments || []);
    setProductivity(productivityRes.data.productivity || []);
  }, [authConfig, payrollMonth]);

  const loadWorkshop = useCallback(async (month = workshopMonth) => {
    const response = await axios.get('/api/hr/workshop/board', {
      ...authConfig,
      params: { month },
    });

    const board = response.data.board || emptyWorkshopBoard();
    setWorkshopBoard(board);
    setProductivitySettingsForm({
      ...emptyProductivitySettings(),
      ...(board.settings || {}),
    });
  }, [authConfig, workshopMonth]);

  const loadRoleMappings = useCallback(async () => {
    if (access?.role !== 'Admin') {
      setRoleMappings([]);
      return;
    }

    const response = await axios.get('/api/hr/access/roles', authConfig);
    setRoleMappings(response.data.roleMappings || []);
  }, [access, authConfig]);

  const loadHrSettings = useCallback(async (year = settingsYear) => {
    if (access?.role === 'Employee') {
      setLeaveTypeSettings([]);
      setCalendarSettings(emptyCalendarSettings());
      return;
    }

    const normalizedYear = Number(year || yearValue()) || yearValue();
    const [leaveTypeRes, calendarRes] = await Promise.all([
      axios.get('/api/hr/settings/leave-types', authConfig),
      axios.get('/api/hr/settings/calendar', {
        ...authConfig,
        params: { year: normalizedYear },
      }),
    ]);

    setLeaveTypeSettings(leaveTypeRes.data.leaveTypes || []);
    setCalendarSettings(calendarRes.data.calendar || { ...emptyCalendarSettings(), year: normalizedYear });
    setHolidayForm({
      holidayId: '',
      holidayDate: `${normalizedYear}-01-01`,
      holidayName: '',
      notes: '',
    });
  }, [access, authConfig, settingsYear]);

  const loadEverything = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const resolvedAccess = await loadAccessAndLookups();
      const employeeList = await loadEmployees('');
      const defaultEmployeeId = employeeList[0]?.EmployeeId || '';
      const defaultEmployeeCode = employeeList[0]?.EmployeeCode || '';
      setAttendanceEmployeeId((current) => current || String(defaultEmployeeId || ''));
      setAttendanceSearchEmployeeCode((current) => current || String(defaultEmployeeCode || ''));
      setAttendanceForm((current) => ({ ...current, employeeId: current.employeeId || String(defaultEmployeeId || '') }));
      setLeaveForm((current) => ({ ...current, employeeId: current.employeeId || String(defaultEmployeeId || '') }));
      setAdjustmentForm((current) => ({ ...current, employeeId: current.employeeId || String(defaultEmployeeId || ''), payrollMonth }));
      setLeaveBalanceEmployeeId((current) => current || String(defaultEmployeeId || ''));
      await Promise.all([
        loadDashboard(payrollMonth),
        loadAttendance(attendanceMonth, ''),
        loadLeaves(String(defaultEmployeeId || '')),
        loadPayroll(payrollMonth),
        resolvedAccess?.role === 'Employee' ? Promise.resolve() : loadJobGroups(),
        resolvedAccess?.role === 'Employee' ? Promise.resolve() : loadWorkshop(workshopMonth),
        resolvedAccess?.role === 'Employee' ? Promise.resolve() : loadHrSettings(settingsYear),
      ]);
    } catch (loadError) {
      showError(loadError.response?.data?.message || 'Failed to load HR module.');
    } finally {
      setLoading(false);
    }
  }, [attendanceMonth, loadAccessAndLookups, loadAttendance, loadDashboard, loadEmployees, loadHrSettings, loadJobGroups, loadLeaves, loadPayroll, loadWorkshop, payrollMonth, settingsYear, showError, workshopMonth]);

  useEffect(() => {
    loadEverythingRef.current = loadEverything;
  }, [loadEverything]);

  useEffect(() => {
    loadEverythingRef.current?.();
  }, [routeRefreshToken]);

  useEffect(() => {
    if (access?.role === 'Admin') {
      loadRoleMappings().catch((mappingError) => {
        showError(mappingError.response?.data?.message || 'Failed to load role mappings.');
      });
    }
  }, [access, loadRoleMappings, showError]);

  useEffect(() => {
    if (access?.role && access.role !== 'Employee') {
      loadHrSettings(settingsYear).catch((settingsError) => {
        showError(settingsError.response?.data?.message || 'Failed to load HR settings.');
      });
    }
  }, [access, loadHrSettings, settingsYear, showError]);

  useEffect(() => {
    setQueuePage(1);
  }, [queueFilter, queuePageSize, queueSearch, queueSort, workshopBoard.employees]);

  useEffect(() => {
    if (queuePage !== queuePagination.currentPage) {
      setQueuePage(queuePagination.currentPage);
    }
  }, [queuePage, queuePagination.currentPage]);

  const toggleQueueEmployee = useCallback((employeeId) => {
    setExpandedQueueEmployees((current) => ({
      ...current,
      [employeeId]: !current[employeeId],
    }));
  }, []);

  const handleEmployeeField = (field) => (event) => {
    const nextValue = event.target.value;
    setEmployeeForm((current) => {
      const selectedDepartment = field === 'departmentId'
        ? lookups.departments.find((department) => String(department.DepartmentId) === String(nextValue))
        : null;
      const vipDesignation = selectedDepartment?.DepartmentName === ADMIN_DEPARTMENT
        ? lookups.designations.find((designation) => (
          String(designation.DepartmentId || '') === String(nextValue)
          && designation.DesignationName === VIP_DESIGNATION
        ))
        : null;
      const nextForm = {
        ...current,
        [field]: nextValue,
        ...(field === 'departmentId' ? { designationId: vipDesignation ? String(vipDesignation.DesignationId) : '', jobGroupIds: [] } : {}),
      };

      if (field === 'designationId') {
        const designation = lookups.designations.find((item) => String(item.DesignationId) === String(nextValue));
        if (designation?.DesignationName !== SERVICE_ENGINEER_DESIGNATION) {
          nextForm.jobGroupIds = [];
        }
      }

      return nextForm;
    });
  };

  const toggleEmployeeJobGroup = useCallback((jobGroupId) => {
    setEmployeeForm((current) => {
      const currentIds = Array.isArray(current.jobGroupIds) ? current.jobGroupIds : [];
      const nextIds = currentIds.includes(String(jobGroupId))
        ? currentIds.filter((value) => String(value) !== String(jobGroupId))
        : [...currentIds, String(jobGroupId)];

      return {
        ...current,
        jobGroupIds: nextIds,
      };
    });
  }, []);

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEmployeeForm((current) => ({
        ...current,
        photoData: String(reader.result || ''),
      }));
    };
    reader.readAsDataURL(file);
  };

  const resetEmployeeForm = () => {
    setEditingEmployeeId(null);
    setEmployeeForm(emptyEmployeeForm());
  };

  const resetJobGroupForm = useCallback(() => {
    setEditingJobGroupId(null);
    setJobGroupForm(emptyJobGroupForm());
  }, []);

  const addJobGroupMember = useCallback(() => {
    if (!jobGroupForm.selectedEmployeeId) {
      return;
    }

    const employee = employeeOptions.find((item) => String(item.EmployeeId) === String(jobGroupForm.selectedEmployeeId));
    if (!employee) {
      return;
    }

    setJobGroupForm((current) => {
      if (current.members.some((member) => String(member.EmployeeId) === String(employee.EmployeeId))) {
        return { ...current, selectedEmployeeId: '' };
      }

      return {
        ...current,
        selectedEmployeeId: '',
        members: [...current.members, {
          EmployeeId: employee.EmployeeId,
          EmployeeCode: employee.EmployeeCode || '',
          FullName: employee.FullName || '',
          DepartmentName: employee.DepartmentName || '',
          DesignationName: employee.DesignationName || employee.RoleName || '',
        }],
      };
    });
  }, [employeeOptions, jobGroupForm.selectedEmployeeId]);

  const removeJobGroupMember = useCallback((employeeId) => {
    setJobGroupForm((current) => ({
      ...current,
      members: current.members.filter((member) => String(member.EmployeeId) !== String(employeeId)),
    }));
  }, []);

  const startEditJobGroup = useCallback((jobGroup) => {
    setEditingJobGroupId(jobGroup.JobGroupId);
    setActiveTab('jobGroups');
    setJobGroupForm({
      groupCode: jobGroup.GroupCode || '',
      groupName: jobGroup.GroupName || '',
      selectedEmployeeId: '',
      members: (jobGroup.Members || []).map((member) => ({
        EmployeeId: member.EmployeeId,
        EmployeeCode: member.EmployeeCode || '',
        FullName: member.FullName || '',
        DepartmentName: member.DepartmentName || '',
        DesignationName: member.DesignationName || '',
      })),
    });
  }, []);

  const saveJobGroup = async (event) => {
    event.preventDefault();

    if (jobGroupForm.members.length < 2 || jobGroupForm.members.length > 5) {
      showError('Please add 2 to 5 employees in the job group.');
      return;
    }

    const payload = {
      groupCode: jobGroupForm.groupCode.trim(),
      groupName: jobGroupForm.groupName.trim(),
      employeeIds: jobGroupForm.members.map((member) => member.EmployeeId),
    };

    try {
      if (editingJobGroupId) {
        await axios.put(`/api/hr/job-groups/${editingJobGroupId}`, payload, authConfig);
        showSuccess('Job group updated successfully.');
      } else {
        await axios.post('/api/hr/job-groups', payload, authConfig);
        showSuccess('Job group created successfully.');
      }

      resetJobGroupForm();
      await loadJobGroups();
    } catch (saveError) {
      showError(
        saveError.response?.data?.message
        || saveError.response?.data?.errors?.[0]?.msg
        || 'Failed to save job group.'
      );
    }
  };

  const deleteJobGroup = async (jobGroup) => {
    const confirmed = window.confirm(`Delete job group "${jobGroup.GroupName}"?`);
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/hr/job-groups/${jobGroup.JobGroupId}`, authConfig);
      if (editingJobGroupId === jobGroup.JobGroupId) {
        resetJobGroupForm();
      }
      showSuccess('Job group deleted successfully.');
      await loadJobGroups();
    } catch (deleteError) {
      showError(deleteError.response?.data?.message || 'Failed to delete job group.');
    }
  };

  const saveEmployee = async (event) => {
    event.preventDefault();

    if (requiresJobGroup && (!Array.isArray(employeeForm.jobGroupIds) || employeeForm.jobGroupIds.length === 0)) {
      showError('Please select at least one job group for Service Engineer.');
      return;
    }

    try {
      if (editingEmployeeId) {
        await axios.put(`/api/hr/employees/${editingEmployeeId}`, employeeForm, authConfig);
        showSuccess('Employee updated successfully.');
      } else {
        await axios.post('/api/hr/employees', employeeForm, authConfig);
        showSuccess('Employee created successfully.');
      }

      resetEmployeeForm();
      await loadEmployees('');
      if (access?.role !== 'Employee') {
        await loadJobGroups();
      }
      await loadDashboard(payrollMonth);
    } catch (saveError) {
      showError(
        saveError.response?.data?.message
        || saveError.response?.data?.errors?.[0]?.msg
        || 'Failed to save employee.'
      );
    }
  };

  const startEditEmployee = (employee) => {
    setEditingEmployeeId(employee.EmployeeId);
    setActiveTab('employees');
    setEmployeeForm({
      employeeCode: employee.EmployeeCode || '',
      photoData: employee.PhotoData || '',
      appUsername: employee.AppUsername || '',
      appPassword: '',
      softwareRole: employee.SoftwareRole || SOFTWARE_ROLES.NONE,
      mobileAccessEnabled: Boolean(employee.MobileAccessEnabled),
      fullName: employee.FullName || '',
      phoneNumber: employee.PhoneNumber || '',
      email: employee.Email || '',
      address: employee.Address || '',
      departmentId: employee.DepartmentId ? String(employee.DepartmentId) : '',
      designationId: employee.DesignationId ? String(employee.DesignationId) : '',
      jobGroupIds: Array.isArray(employee.JobGroupIds)
        ? employee.JobGroupIds.map((value) => String(value))
        : Array.isArray(employee.JobGroups)
          ? employee.JobGroups.map((group) => String(group.JobGroupId))
          : [],
      roleId: employee.RoleId ? String(employee.RoleId) : '',
      joiningDate: employee.JoiningDate ? String(employee.JoiningDate).slice(0, 10) : todayValue(),
      basicSalary: employee.BasicSalary ?? employee.Salary ?? '',
      allowance: employee.Allowance ?? '',
      deduction: employee.Deduction ?? '',
      overtimeRate: employee.OvertimeRate ?? employee.RatePerHour ?? '',
      employmentStatus: employee.EmploymentStatus || 'Active',
      notes: employee.Notes || '',
    });
  };

  const deleteEmployee = async (employee) => {
    const confirmed = window.confirm(`Delete or deactivate ${employee.FullName}?`);
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/hr/employees/${employee.EmployeeId}`, authConfig);
      showSuccess('Employee record updated.');
      await loadEmployees('');
      if (access?.role !== 'Employee') {
        await loadJobGroups();
      }
      await loadDashboard(payrollMonth);
    } catch (deleteError) {
      showError(deleteError.response?.data?.message || 'Failed to delete employee.');
    }
  };

  const saveAttendance = async (event) => {
    event.preventDefault();

    try {
      await axios.post('/api/hr/attendance/manual', attendanceForm, authConfig);
      showSuccess('Attendance saved successfully.');
      await loadAttendance(attendanceMonth, attendanceEmployeeId);
      await loadDashboard(payrollMonth);
    } catch (attendanceError) {
      showError(attendanceError.response?.data?.message || 'Failed to save attendance.');
    }
  };

  const loadAttendanceReport = async () => {
    const employeeCode = attendanceSearchEmployeeCode.trim();

    if (employeeCode !== attendanceEmployeeCode || attendanceEmployeeId) {
      setAttendanceEmployeeId('');
      setAttendanceEmployeeCode(employeeCode);
      return;
    }

    try {
      await loadAttendance(attendanceMonth, '', employeeCode);
    } catch (loadError) {
      showError(loadError.response?.data?.message || 'Failed to refresh attendance report.');
    }
  };

  const quickAttendanceAction = async (type) => {
    const endpoint = type === 'check-in' ? '/api/hr/attendance/check-in' : '/api/hr/attendance/check-out';
    const reportMonth = String(attendanceForm.attendanceDate || todayValue()).slice(0, 7);
    const reportEmployeeId = attendanceForm.employeeId || attendanceEmployeeId || access?.employeeId || '';

    try {
      await axios.post(endpoint, {
        employeeId: attendanceForm.employeeId,
        attendanceDate: attendanceForm.attendanceDate,
        notes: attendanceForm.notes,
      }, authConfig);
      showSuccess(type === 'check-in' ? 'Check-in recorded.' : 'Check-out recorded.');
      setAttendanceMonth(reportMonth);
      setAttendanceEmployeeId(reportEmployeeId);
      await loadAttendance(reportMonth, reportEmployeeId || undefined);
      await loadDashboard(payrollMonth);
    } catch (quickError) {
      showError(quickError.response?.data?.message || `Failed to ${type}.`);
    }
  };

  const submitLeaveRequest = async (event) => {
    event.preventDefault();

    try {
      await axios.post('/api/hr/leave/requests', leaveForm, authConfig);
      showSuccess('Leave request submitted successfully.');
      setLeaveForm((current) => ({
        ...current,
        startDate: todayValue(),
        endDate: todayValue(),
        reason: '',
      }));
      await loadLeaves(selectedBalanceEmployeeId);
      await loadDashboard(payrollMonth);
    } catch (leaveError) {
      showError(
        leaveError.response?.data?.message
        || leaveError.response?.data?.errors?.[0]?.msg
        || 'Failed to submit leave request.'
      );
    }
  };

  const decideLeave = async (leaveRequestId, decision) => {
    try {
      await axios.post(`/api/hr/leave/requests/${leaveRequestId}/decision`, {
        decision,
        remarks: decision === 'Rejected' ? 'Rejected from HR workspace' : 'Approved from HR workspace',
      }, authConfig);
      showSuccess(`Leave request ${decision.toLowerCase()} successfully.`);
      await loadLeaves(selectedBalanceEmployeeId);
      await loadDashboard(payrollMonth);
    } catch (decisionError) {
      showError(decisionError.response?.data?.message || `Failed to ${decision.toLowerCase()} leave request.`);
    }
  };

  const addPayrollAdjustment = async (event) => {
    event.preventDefault();

    try {
      await axios.post('/api/hr/payroll/adjustments', adjustmentForm, authConfig);
      showSuccess('Payroll adjustment saved.');
      setAdjustmentForm((current) => ({
        ...current,
        amount: '',
        notes: '',
      }));
      await loadPayroll(payrollMonth);
    } catch (adjustmentError) {
      showError(adjustmentError.response?.data?.message || 'Failed to save payroll adjustment.');
    }
  };

  const generatePayroll = async () => {
    try {
      await axios.post('/api/hr/payroll/generate', { month: payrollMonth }, authConfig);
      showSuccess(`Payroll generated for ${payrollMonth}.`);
      await loadPayroll(payrollMonth);
      await loadDashboard(payrollMonth);
    } catch (generateError) {
      showError(generateError.response?.data?.message || 'Failed to generate payroll.');
    }
  };

  const openPayrollPaymentDialog = async (run) => {
    try {
      setPayrollPaymentDialog((current) => ({
        ...current,
        open: true,
        loading: true,
        run,
      }));

      const [detailRes, accountsRes] = await Promise.all([
        axios.get(`/api/hr/payroll/runs/${run.PayrollRunId}`, authConfig),
        axios.get('/api/accounting/chart-of-accounts', authConfig),
      ]);

      const accounts = (accountsRes.data.accounts || []).filter((account) => (
        account.IsPostable
        && account.IsActive
        && ['Cash', 'Bank', 'MobileBank'].includes(account.AccountClass)
      ));
      const defaultAccountCode = accounts.some((account) => account.AccountCode === '11101')
        ? '11101'
        : (accounts[0]?.AccountCode || '11101');
      const employeesForPayment = detailRes.data.employees || [];

      setPayrollPaymentDialog({
        open: true,
        loading: false,
        run: detailRes.data.run || run,
        employees: employeesForPayment,
        payments: detailRes.data.payments || [],
        accounts,
        paymentDate: todayValue(),
        paymentAccountCode: defaultAccountCode,
        notes: '',
        entries: employeesForPayment.map((employee) => ({
          payrollRunEmployeeId: employee.PayrollRunEmployeeId,
          employeeId: employee.EmployeeId,
          fullName: employee.FullName,
          dueAmount: Number(employee.BalanceAmount || 0),
          amount: Number(employee.BalanceAmount || 0),
        })),
      });
    } catch (dialogError) {
      setPayrollPaymentDialog(emptyPayrollPaymentDialog());
      showError(dialogError.response?.data?.message || 'Failed to load payroll payment details.');
    }
  };

  const closePayrollPaymentDialog = () => {
    setPayrollPaymentDialog(emptyPayrollPaymentDialog());
  };

  const updatePayrollPaymentEntry = (payrollRunEmployeeId, nextAmount) => {
    setPayrollPaymentDialog((current) => ({
      ...current,
      entries: current.entries.map((entry) => (
        entry.payrollRunEmployeeId === payrollRunEmployeeId
          ? { ...entry, amount: nextAmount }
          : entry
      )),
    }));
  };

  const submitPayrollPayment = async () => {
    try {
      if (!payrollPaymentDialog.run?.PayrollRunId) {
        showError('Payroll run is missing for salary payment.');
        return;
      }

      const entries = payrollPaymentDialog.entries
        .map((entry) => ({
          payrollRunEmployeeId: entry.payrollRunEmployeeId,
          amount: Number(entry.amount || 0),
          dueAmount: Number(entry.dueAmount || 0),
          fullName: entry.fullName,
        }))
        .filter((entry) => entry.amount > 0);

      if (!entries.length) {
        showError('Enter payment amount for at least one employee.');
        return;
      }

      const invalidEntry = entries.find((entry) => entry.amount > entry.dueAmount);
      if (invalidEntry) {
        showError(`Payment cannot exceed due for ${invalidEntry.fullName}.`);
        return;
      }

      setPayrollPaymentDialog((current) => ({ ...current, loading: true }));

      await axios.post(`/api/hr/payroll/runs/${payrollPaymentDialog.run.PayrollRunId}/pay`, {
        paymentDate: payrollPaymentDialog.paymentDate,
        paymentAccountCode: payrollPaymentDialog.paymentAccountCode,
        notes: payrollPaymentDialog.notes,
        entries: entries.map((entry) => ({
          payrollRunEmployeeId: entry.payrollRunEmployeeId,
          amount: entry.amount,
        })),
      }, authConfig);

      closePayrollPaymentDialog();
      showSuccess('Salary payment posted and accounting voucher created.');
      await loadPayroll(payrollMonth);
      await loadDashboard(payrollMonth);
    } catch (paymentError) {
      setPayrollPaymentDialog((current) => ({ ...current, loading: false }));
      showError(paymentError.response?.data?.message || 'Failed to pay salary.');
    }
  };

  const saveProductivitySettings = async (event) => {
    event.preventDefault();

    try {
      await axios.put('/api/hr/settings/productivity', productivitySettingsForm, authConfig);
      showSuccess('Workshop productivity rules updated.');
      await loadWorkshop(workshopMonth);
      await loadPayroll(payrollMonth);
    } catch (settingsError) {
      showError(settingsError.response?.data?.message || 'Failed to update productivity settings.');
    }
  };

  const saveRoleMapping = async (event) => {
    event.preventDefault();

    try {
      await axios.post('/api/hr/access/roles', roleForm, authConfig);
      showSuccess('Role mapping saved.');
      setRoleForm({ userEmail: '', appRoleName: 'Employee', employeeId: '' });
      await loadRoleMappings();
    } catch (roleError) {
      showError(roleError.response?.data?.message || 'Failed to save role mapping.');
    }
  };

  const startEditLeaveType = (leaveType) => {
    setLeaveTypeForm({
      leaveTypeId: String(leaveType.LeaveTypeId || ''),
      leaveTypeName: leaveType.LeaveTypeName || '',
      leaveCode: leaveType.LeaveCode || '',
      annualAllocation: String(leaveType.AnnualAllocation ?? ''),
      isPaid: Boolean(leaveType.IsPaid),
      isActive: leaveType.IsActive !== false,
    });
  };

  const resetLeaveTypeForm = () => {
    setLeaveTypeForm(emptyLeaveTypeForm());
  };

  const saveLeaveTypeSettings = async (event) => {
    event.preventDefault();

    try {
      const payload = {
        leaveTypeName: leaveTypeForm.leaveTypeName,
        leaveCode: leaveTypeForm.leaveCode,
        annualAllocation: leaveTypeForm.annualAllocation,
        isPaid: leaveTypeForm.isPaid,
        isActive: leaveTypeForm.isActive,
      };

      if (leaveTypeForm.leaveTypeId) {
        await axios.put(`/api/hr/settings/leave-types/${leaveTypeForm.leaveTypeId}`, payload, authConfig);
        showSuccess('Leave type updated.');
      } else {
        await axios.post('/api/hr/settings/leave-types', payload, authConfig);
        showSuccess('Leave type created.');
      }

      resetLeaveTypeForm();
      await Promise.all([
        loadHrSettings(settingsYear),
        loadAccessAndLookups(),
      ]);
    } catch (leaveTypeError) {
      showError(leaveTypeError.response?.data?.message || 'Failed to save leave type.');
    }
  };

  const toggleWeekendDay = (weekdayValue) => {
    setCalendarSettings((current) => {
      const exists = current.weekendDays.includes(weekdayValue);
      return {
        ...current,
        weekendDays: exists
          ? current.weekendDays.filter((value) => value !== weekdayValue)
          : [...current.weekendDays, weekdayValue].sort((left, right) => left - right),
      };
    });
  };

  const saveWeekendSettings = async () => {
    try {
      const response = await axios.put('/api/hr/settings/calendar/weekends', {
        year: calendarSettings.year,
        weekendDays: calendarSettings.weekendDays,
      }, authConfig);
      setCalendarSettings(response.data.calendar || emptyCalendarSettings());
      showSuccess('Weekend calendar updated.');
      await loadPayroll(payrollMonth);
    } catch (calendarError) {
      showError(calendarError.response?.data?.message || 'Failed to update weekend calendar.');
    }
  };

  const selectCalendarDate = (dateKey) => {
    const holiday = holidayMap.get(dateKey);
    if (holiday) {
      setHolidayForm({
        holidayId: String(holiday.HolidayId || ''),
        holidayDate: holiday.HolidayDate,
        holidayName: holiday.HolidayName || '',
        notes: holiday.Notes || '',
      });
      return;
    }

    setHolidayForm({
      holidayId: '',
      holidayDate: dateKey,
      holidayName: '',
      notes: '',
    });
  };

  const resetHolidayForm = () => {
    setHolidayForm({
      ...emptyHolidayForm(),
      holidayDate: calendarSettings.year === yearValue() ? todayValue() : `${calendarSettings.year}-01-01`,
    });
  };

  const saveHoliday = async (event) => {
    event.preventDefault();

    try {
      const payload = {
        holidayDate: holidayForm.holidayDate,
        holidayName: holidayForm.holidayName,
        notes: holidayForm.notes,
      };

      const response = holidayForm.holidayId
        ? await axios.put(`/api/hr/settings/calendar/holidays/${holidayForm.holidayId}`, payload, authConfig)
        : await axios.post('/api/hr/settings/calendar/holidays', payload, authConfig);

      setCalendarSettings(response.data.calendar || emptyCalendarSettings());
      resetHolidayForm();
      showSuccess(holidayForm.holidayId ? 'Holiday updated.' : 'Holiday saved.');
      await loadPayroll(payrollMonth);
    } catch (holidayError) {
      showError(holidayError.response?.data?.message || 'Failed to save holiday.');
    }
  };

  const deleteHoliday = async () => {
    if (!holidayForm.holidayId) {
      return;
    }

    try {
      const response = await axios.delete(`/api/hr/settings/calendar/holidays/${holidayForm.holidayId}`, authConfig);
      setCalendarSettings(response.data.calendar || emptyCalendarSettings());
      resetHolidayForm();
      showSuccess('Holiday removed.');
      await loadPayroll(payrollMonth);
    } catch (holidayError) {
      showError(holidayError.response?.data?.message || 'Failed to remove holiday.');
    }
  };

  const openReportWorkspace = useCallback(async (reportType) => {
    setReportLoading(true);
    try {
      const response = await axios.get(`/api/hr/reports/${reportType}`, {
        ...authConfig,
        params: {
          month: reportMonth,
        },
      });

      setReportWorkspace({
        reportType,
        rows: response.data.report || [],
        summary: response.data.summary || {},
        loadedAt: new Date().toISOString(),
      });
      showSuccess(`${reportType} report loaded.`);
    } catch (reportError) {
      showError(reportError.response?.data?.message || 'Failed to open report.');
    } finally {
      setReportLoading(false);
    }
  }, [authConfig, reportMonth, showError, showSuccess]);

  const reportSummaryCards = useMemo(() => {
    const rows = reportWorkspace.rows || [];
    const summary = reportWorkspace.summary || {};

    if (reportWorkspace.reportType === 'employees') {
      const activeCount = rows.filter((row) => String(row.EmploymentStatus || '').toLowerCase() === 'active').length;
      const departmentCount = new Set(rows.map((row) => row.Department).filter(Boolean)).size;
      const totalBasic = rows.reduce((sum, row) => sum + Number(row.BasicSalary || 0), 0);
      return [
        { label: 'Employees', value: rows.length, tone: 'emerald' },
        { label: 'Active', value: activeCount, tone: 'blue' },
        { label: 'Departments', value: departmentCount, tone: 'amber' },
        { label: 'Basic Salary', value: currency(totalBasic), tone: 'rose' },
      ];
    }

    if (reportWorkspace.reportType === 'attendance') {
      const presentCount = rows.filter((row) => String(row.AttendanceStatus || '').toLowerCase() === 'present').length;
      const lateMinutes = rows.reduce((sum, row) => sum + Number(row.LateMinutes || 0), 0);
      const overtimeMinutes = rows.reduce((sum, row) => sum + Number(row.OvertimeMinutes || 0), 0);
      return [
        { label: 'Entries', value: rows.length, tone: 'emerald' },
        { label: 'Present', value: presentCount, tone: 'blue' },
        { label: 'Late Minutes', value: lateMinutes, tone: 'amber' },
        { label: 'Overtime Minutes', value: overtimeMinutes, tone: 'rose' },
      ];
    }

    if (reportWorkspace.reportType === 'leaves') {
      const approvedCount = rows.filter((row) => String(row.RequestStatus || '').toLowerCase() === 'approved').length;
      const pendingCount = rows.filter((row) => String(row.RequestStatus || '').toLowerCase() === 'pending').length;
      const totalDays = rows.reduce((sum, row) => sum + Number(row.DaysRequested || 0), 0);
      return [
        { label: 'Requests', value: rows.length, tone: 'emerald' },
        { label: 'Approved', value: approvedCount, tone: 'blue' },
        { label: 'Pending', value: pendingCount, tone: 'amber' },
        { label: 'Days Requested', value: Number(totalDays.toFixed(2)), tone: 'rose' },
      ];
    }

    if (reportWorkspace.reportType === 'payroll') {
      return [
        { label: 'Employees', value: summary.employeeCount || rows.length, tone: 'emerald' },
        { label: 'Gross Salary', value: currency(summary.totalGross || 0), tone: 'blue' },
        { label: 'Net Salary', value: currency(summary.totalNet || 0), tone: 'amber' },
        { label: 'OT Hours', value: hoursLabel(summary.totalOvertimeHours || 0), tone: 'rose' },
      ];
    }

    return [];
  }, [reportWorkspace]);

  const reportColumns = useMemo(() => {
    if (reportWorkspace.reportType === 'employees') {
      return [
        { key: 'EmployeeCode', label: 'Code' },
        { key: 'FullName', label: 'Employee' },
        { key: 'PhoneNumber', label: 'Phone' },
        { key: 'Email', label: 'Email' },
        { key: 'Department', label: 'Department' },
        { key: 'Designation', label: 'Designation' },
        { key: 'JoiningDate', label: 'Joining Date', render: (row) => formatHumanDate(row.JoiningDate) },
        { key: 'BasicSalary', label: 'Basic Salary', render: (row) => currency(row.BasicSalary || 0) },
        { key: 'EmploymentStatus', label: 'Status' },
      ];
    }

    if (reportWorkspace.reportType === 'attendance') {
      return [
        { key: 'FullName', label: 'Employee' },
        { key: 'AttendanceDate', label: 'Date', render: (row) => formatHumanDate(row.AttendanceDate) },
        { key: 'CheckInTime', label: 'Check In', render: (row) => formatHumanDateTime(row.CheckInTime) },
        { key: 'CheckOutTime', label: 'Check Out', render: (row) => formatHumanDateTime(row.CheckOutTime) },
        { key: 'LateMinutes', label: 'Late Min' },
        { key: 'OvertimeMinutes', label: 'OT Min' },
        { key: 'AttendanceStatus', label: 'Status' },
      ];
    }

    if (reportWorkspace.reportType === 'leaves') {
      return [
        { key: 'FullName', label: 'Employee' },
        { key: 'LeaveTypeName', label: 'Leave Type' },
        { key: 'StartDate', label: 'Start Date', render: (row) => formatHumanDate(row.StartDate) },
        { key: 'EndDate', label: 'End Date', render: (row) => formatHumanDate(row.EndDate) },
        { key: 'DaysRequested', label: 'Days' },
        { key: 'RequestStatus', label: 'Status' },
        { key: 'ReviewedByEmail', label: 'Reviewed By' },
      ];
    }

    if (reportWorkspace.reportType === 'payroll') {
      return [
        { key: 'EmployeeCode', label: 'Code' },
        { key: 'FullName', label: 'Employee' },
        { key: 'DepartmentName', label: 'Department' },
        { key: 'DesignationName', label: 'Designation' },
        { key: 'BasicSalary', label: 'Basic', render: (row) => currency(row.BasicSalary || 0) },
        { key: 'AllowanceAmount', label: 'Allowance', render: (row) => currency(row.AllowanceAmount || 0) },
        { key: 'DeductionAmount', label: 'Deduction', render: (row) => currency(row.DeductionAmount || 0) },
        { key: 'OvertimeAmount', label: 'OT Amount', render: (row) => currency(row.OvertimeAmount || 0) },
        { key: 'NetSalary', label: 'Net Salary', render: (row) => currency(row.NetSalary || 0) },
      ];
    }

    return [];
  }, [reportWorkspace.reportType]);

  useEffect(() => {
    if (activeTab === 'reports' && access?.role !== 'Employee' && !reportWorkspace.reportType && !reportLoading) {
      openReportWorkspace('employees');
    }
  }, [access, activeTab, openReportWorkspace, reportLoading, reportWorkspace.reportType]);

  if (loading) {
    return (
      <div className="home-container">
        <Sidebar onLogout={onLogout} />
        <main className="hr-page">
          <div className="hr-loading">Loading HR module...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="hr-page">
        <header className="hr-header">
          <div className="hr-header-main">
            <button className="app-back-button" type="button" onClick={() => navigate('/')} aria-label="Back to home" />
            <div>
              <h1>Workshop HR Command Center</h1>
              <p className="hr-subtitle">
                Employees, attendance, leave, payroll, permissions, and technician productivity in one place.
              </p>
            </div>
          </div>
          <div className="hr-header-side">
            <span className={`hr-role-badge hr-role-${String(access?.role || '').toLowerCase()}`}>{access?.role}</span>
            <button className="hr-refresh" type="button" onClick={loadEverything}>Refresh</button>
          </div>
        </header>

        {(error || success) && (
          <div className={`hr-notice ${error ? 'error' : 'success'}`}>
            {error || success}
          </div>
        )}

        <nav className="hr-tabs" aria-label="HR workspace tabs">
          {[
            ['overview', 'Overview'],
            ['employees', 'Employees'],
            ...(access?.role === 'Employee' ? [] : [['jobGroups', 'Job Groups']]),
            ['attendance', 'Attendance'],
            ['leave', 'Leave'],
            ['payroll', 'Payroll'],
            ...(access?.role === 'Employee' ? [] : [['workshop', 'Workshop']]),
            ...(access?.role === 'Employee' ? [] : [['settings', 'Settings']]),
            ['reports', 'Reports'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={activeTab === id ? 'active' : ''}
              onClick={() => setActiveTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        {activeTab === 'overview' && (
          <section className="hr-panel">
            <div className="hr-stat-grid">
              <article className="hr-stat-card">
                <span>Active Employees</span>
                <strong>{dashboard.summary.ActiveEmployees || 0}</strong>
                <small>Total records: {dashboard.summary.TotalEmployees || 0}</small>
              </article>
              <article className="hr-stat-card">
                <span>Present Today</span>
                <strong>{dashboard.attendanceToday.PresentToday || 0}</strong>
                <small>Late today: {dashboard.attendanceToday.LateToday || 0}</small>
              </article>
              <article className="hr-stat-card">
                <span>Leave Workflow</span>
                <strong>{dashboard.leaveToday.PendingLeaveRequests || 0}</strong>
                <small>On leave today: {dashboard.leaveToday.OnLeaveToday || 0}</small>
              </article>
              <article className="hr-stat-card">
                <span>Latest Payroll</span>
                <strong>{dashboard.latestPayroll?.PayrollMonth || 'None'}</strong>
                <small>{currency(dashboard.latestPayroll?.TotalNetSalary || 0)}</small>
              </article>
            </div>

            <div className="hr-two-column">
              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Top Performers</h2>
                  <span>{dashboard.currentMonth}</span>
                </div>
                <div className="hr-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Hours</th>
                        <th>Jobs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.topPerformers.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="hr-empty-cell">No workshop activity this month.</td>
                        </tr>
                      ) : dashboard.topPerformers.map((item) => (
                        <tr key={item.EmployeeId}>
                          <td>{item.FullName}</td>
                          <td>{item.DepartmentName || 'Workshop'}</td>
                          <td>{Number(item.LoggedHours || 0).toFixed(2)}h</td>
                          <td>{item.JobCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Productivity Snapshot</h2>
                  <span>Job card integration</span>
                </div>
                <div className="hr-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Hours</th>
                        <th>Revenue</th>
                        <th>Rev/Hr</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productivity.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="hr-empty-cell">No productivity data available.</td>
                        </tr>
                      ) : productivity.slice(0, 6).map((item) => (
                        <tr key={item.EmployeeId}>
                          <td>{item.FullName}</td>
                          <td>{Number(item.LoggedHours || 0).toFixed(2)}h</td>
                          <td>{currency(item.LaborRevenue || 0)}</td>
                          <td>{currency(item.RevenuePerHour || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </section>
        )}

        {activeTab === 'employees' && (
          <section className="hr-panel hr-two-column wide-left">
            <section className="hr-card">
              <div className="hr-card-header">
                <h2>Employee Directory</h2>
                <div className="hr-inline-actions">
                  <input
                    className="hr-search"
                    value={employeeSearch}
                    onChange={(event) => setEmployeeSearch(event.target.value)}
                    placeholder="Search employee, phone, email, department"
                  />
                  <button type="button" onClick={() => loadEmployees(employeeSearch)}>Search</button>
                </div>
              </div>
              <div className="hr-list-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Designation</th>
                      <th>Software Role</th>
                      <th>Joining</th>
                      <th>Salary</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="hr-empty-cell">No employees found.</td>
                      </tr>
                    ) : employees.map((employee) => (
                      <tr key={employee.EmployeeId}>
                        <td>
                          <div className="hr-identity">
                            {employee.PhotoData ? <img src={employee.PhotoData} alt={employee.FullName} /> : <span>{employee.FullName?.[0] || 'E'}</span>}
                            <div>
                              <strong>{employee.FullName}</strong>
                              <small>{employee.PhoneNumber}</small>
                            </div>
                          </div>
                        </td>
                        <td>{employee.DepartmentName || '-'}</td>
                        <td>
                          <div>
                            <div>{employee.DesignationName || employee.RoleName || '-'}</div>
                            {employee.JobGroupName && <small>{employee.JobGroupName}</small>}
                          </div>
                        </td>
                        <td>{getSoftwareRoleLabel(employee.SoftwareRole)}</td>
                        <td>{employee.JoiningDate ? new Date(employee.JoiningDate).toLocaleDateString() : '-'}</td>
                        <td>{currency(employee.BasicSalary || employee.Salary || 0)}</td>
                        <td><span className={`hr-status hr-status-${String(employee.EmploymentStatus || '').toLowerCase()}`}>{employee.EmploymentStatus || 'Unknown'}</span></td>
                        <td className="hr-row-actions">
                          <button type="button" onClick={() => startEditEmployee(employee)}>Edit</button>
                          {access?.role === 'Admin' && (
                            <button type="button" className="danger" onClick={() => deleteEmployee(employee)}>Delete</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="hr-card">
              <div className="hr-card-header">
                <h2>{editingEmployeeId ? 'Edit Employee' : 'Add Employee'}</h2>
                {editingEmployeeId && <button type="button" onClick={resetEmployeeForm}>New</button>}
              </div>
              <form className="hr-form" onSubmit={saveEmployee}>
                <div className="hr-photo-picker">
                  {employeeForm.photoData ? <img src={employeeForm.photoData} alt="Employee profile" /> : <div className="placeholder">Photo</div>}
                  <label>
                    Upload Photo
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} />
                  </label>
                </div>
                <div className="hr-form-grid">
                  <label>
                    Employee Code
                    <input value={employeeForm.employeeCode} onChange={handleEmployeeField('employeeCode')} />
                  </label>
                  <label>
                    Full Name
                    <input value={employeeForm.fullName} onChange={handleEmployeeField('fullName')} required />
                  </label>
                  <label>
                    Phone
                    <input value={employeeForm.phoneNumber} onChange={handleEmployeeField('phoneNumber')} required />
                  </label>
                  <label>
                    Email
                    <input type="email" value={employeeForm.email} onChange={handleEmployeeField('email')} />
                  </label>
                  <label>
                    App Username
                    <input value={employeeForm.appUsername} onChange={handleEmployeeField('appUsername')} placeholder="software and mobile login username" />
                  </label>
                  <label>
                    App Password
                    <input type="password" value={employeeForm.appPassword} onChange={handleEmployeeField('appPassword')} placeholder={editingEmployeeId ? 'Leave blank to keep current password' : 'Minimum 6 characters'} />
                  </label>
                  <label>
                    Software Role
                    <select value={employeeForm.softwareRole} onChange={handleEmployeeField('softwareRole')}>
                      {(lookups.softwareRoles || []).length === 0 && (
                        <option value={SOFTWARE_ROLES.NONE}>No Role</option>
                      )}
                      {(lookups.softwareRoles || []).map((role) => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Department
                    <select value={employeeForm.departmentId} onChange={handleEmployeeField('departmentId')}>
                      <option value="">Select department</option>
                      {lookups.departments.map((department) => (
                        <option key={department.DepartmentId} value={department.DepartmentId}>{department.DepartmentName}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Designation
                    <select value={employeeForm.designationId} onChange={handleEmployeeField('designationId')}>
                      <option value="">Select designation</option>
                      {visibleDesignations.map((designation) => (
                        <option key={designation.DesignationId} value={designation.DesignationId}>{designation.DesignationName}</option>
                      ))}
                    </select>
                  </label>
                  {requiresJobGroup && (
                    <label>
                      Job Groups
                      <div className="hr-job-group-checklist">
                        {(lookups.jobGroups || []).length === 0 ? (
                          <div className="hr-empty-panel">No job groups found.</div>
                        ) : (lookups.jobGroups || []).map((group) => (
                          <label key={group.JobGroupId} className="hr-job-group-option">
                            <input
                              type="checkbox"
                              checked={(employeeForm.jobGroupIds || []).includes(String(group.JobGroupId))}
                              onChange={() => toggleEmployeeJobGroup(group.JobGroupId)}
                            />
                            <span>{group.JobGroupName}</span>
                          </label>
                        ))}
                      </div>
                    </label>
                  )}
                  <label>
                    Workshop Role
                    <select value={employeeForm.roleId} onChange={handleEmployeeField('roleId')} required>
                      <option value="">Select workshop role</option>
                      {lookups.workshopRoles.map((role) => (
                        <option key={role.RoleId} value={role.RoleId}>{role.RoleName}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Joining Date
                    <input type="date" value={employeeForm.joiningDate} onChange={handleEmployeeField('joiningDate')} required />
                  </label>
                  <label>
                    Basic Salary
                    <input type="number" step="0.01" value={employeeForm.basicSalary} onChange={handleEmployeeField('basicSalary')} />
                  </label>
                  <label>
                    Allowance
                    <input type="number" step="0.01" value={employeeForm.allowance} onChange={handleEmployeeField('allowance')} />
                  </label>
                  <label>
                    Deduction
                    <input type="number" step="0.01" value={employeeForm.deduction} onChange={handleEmployeeField('deduction')} />
                  </label>
                  <label>
                    Overtime Rate / Hour
                    <input type="number" step="0.01" value={employeeForm.overtimeRate} onChange={handleEmployeeField('overtimeRate')} />
                  </label>
                  <label className="full-span">
                    Employment Status
                    <select value={employeeForm.employmentStatus} onChange={handleEmployeeField('employmentStatus')}>
                      <option value="Active">Active</option>
                      <option value="Resigned">Resigned</option>
                      <option value="Terminated">Terminated</option>
                    </select>
                  </label>
                  <label className="full-span">
                    Address
                    <textarea rows="3" value={employeeForm.address} onChange={handleEmployeeField('address')} />
                  </label>
                  <label className="full-span">
                    Notes
                    <textarea rows="4" value={employeeForm.notes} onChange={handleEmployeeField('notes')} />
                  </label>
                  <label className="full-span hr-inline-check">
                    <input
                      type="checkbox"
                      checked={employeeForm.mobileAccessEnabled}
                      onChange={(event) => setEmployeeForm((current) => ({ ...current, mobileAccessEnabled: event.target.checked }))}
                    />
                    Enable employee mobile app login
                  </label>
                </div>
                <div className="hr-form-actions">
                  <button type="submit">{editingEmployeeId ? 'Update Employee' : 'Create Employee'}</button>
                  <button type="button" className="secondary" onClick={resetEmployeeForm}>Reset</button>
                </div>
              </form>
            </section>
          </section>
        )}

        {activeTab === 'jobGroups' && access?.role !== 'Employee' && (
          <section className="hr-panel hr-two-column wide-left">
            <section className="hr-card">
              <div className="hr-card-header">
                <div>
                  <h2>Job Groups</h2>
                  <span>Create a team with 2 to 5 employees for one job.</span>
                </div>
                <div className="hr-mini-stats">
                  <div><strong>{jobGroups.length}</strong><span>Groups</span></div>
                </div>
              </div>
              <div className="hr-list-table">
                <table>
                  <thead>
                    <tr>
                      <th>Team Code</th>
                      <th>Group Name</th>
                      <th>Employees</th>
                      <th>Count</th>
                      <th>Updated</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobGroups.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="hr-empty-cell">No job groups created yet.</td>
                      </tr>
                    ) : jobGroups.map((jobGroup) => (
                      <tr key={jobGroup.JobGroupId}>
                        <td><strong>{jobGroup.GroupCode || '-'}</strong></td>
                        <td>
                          <strong>{jobGroup.GroupName}</strong>
                        </td>
                        <td>
                          <div className="hr-members-inline">
                            {(jobGroup.Members || []).map((member) => (
                              <span key={`${jobGroup.JobGroupId}-${member.EmployeeId}`} className="hr-member-pill">
                                {member.FullName}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{jobGroup.EmployeeCount || jobGroup.Members?.length || 0}</td>
                        <td>{jobGroup.UpdatedDate ? formatHumanDate(jobGroup.UpdatedDate) : '-'}</td>
                        <td className="hr-row-actions">
                          <button type="button" onClick={() => startEditJobGroup(jobGroup)}>Edit</button>
                          <button type="button" className="danger" onClick={() => deleteJobGroup(jobGroup)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="hr-card">
              <div className="hr-card-header">
                <h2>{editingJobGroupId ? 'Edit Job Group' : 'Create Job Group'}</h2>
                {editingJobGroupId && <button type="button" onClick={resetJobGroupForm}>New</button>}
              </div>
              <form className="hr-form" onSubmit={saveJobGroup}>
                <div className="hr-form-grid">
                  <label>
                    Team Code
                    <input
                      value={jobGroupForm.groupCode}
                      onChange={(event) => setJobGroupForm((current) => ({ ...current, groupCode: event.target.value }))}
                      placeholder="Example: T-101"
                      required
                    />
                  </label>
                  <label className="full-span">
                    Job Group Name
                    <input
                      value={jobGroupForm.groupName}
                      onChange={(event) => setJobGroupForm((current) => ({ ...current, groupName: event.target.value }))}
                      placeholder="Example: Engine Repair Team A"
                      required
                    />
                  </label>
                </div>

                <div className="hr-subcard">
                  <div className="hr-card-header">
                    <div>
                      <h2>Add Employees</h2>
                      <span>Select employee, then click add. Minimum 2, maximum 5.</span>
                    </div>
                  </div>
                  <div className="hr-job-group-builder">
                    <select
                      value={jobGroupForm.selectedEmployeeId}
                      onChange={(event) => setJobGroupForm((current) => ({ ...current, selectedEmployeeId: event.target.value }))}
                    >
                      <option value="">Select employee</option>
                      {availableJobGroupEmployees.map((employee) => (
                        <option key={employee.EmployeeId} value={employee.EmployeeId}>
                          {[employee.EmployeeCode, employee.FullName, employee.DepartmentName].filter(Boolean).join(' - ')}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={addJobGroupMember} disabled={!jobGroupForm.selectedEmployeeId || jobGroupForm.members.length >= 5}>
                      Add
                    </button>
                  </div>

                  <div className="hr-selected-members">
                    {jobGroupForm.members.length === 0 ? (
                      <div className="hr-empty-panel">No employees added yet.</div>
                    ) : jobGroupForm.members.map((member) => (
                      <div key={member.EmployeeId} className="hr-selected-member">
                        <div>
                          <strong>{[member.EmployeeCode, member.FullName].filter(Boolean).join(' - ')}</strong>
                          <small>{[member.DepartmentName, member.DesignationName].filter(Boolean).join(' | ') || 'Workshop employee'}</small>
                        </div>
                        <button type="button" className="secondary" onClick={() => removeJobGroupMember(member.EmployeeId)}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hr-form-actions">
                  <button type="submit">{editingJobGroupId ? 'Update Job Group' : 'Create Job Group'}</button>
                  <button type="button" className="secondary" onClick={resetJobGroupForm}>Reset</button>
                </div>
              </form>
            </section>
          </section>
        )}

        {activeTab === 'attendance' && (
          <section className="hr-panel hr-two-column wide-left">
            <section className="hr-card">
              <div className="hr-card-header">
                <h2>Attendance Report</h2>
                <div className="hr-inline-actions">
                  <input type="month" value={attendanceMonth} onChange={(event) => setAttendanceMonth(event.target.value)} />
                  <input
                    type="text"
                    placeholder="Search by Employee Code"
                    value={attendanceSearchEmployeeCode}
                    onChange={(event) => setAttendanceSearchEmployeeCode(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        loadAttendanceReport();
                      }
                    }}
                  />
                  <select value={attendanceEmployeeId} onChange={(event) => {
                    const selectedEmployee = employeeOptions.find((employee) => String(employee.EmployeeId) === String(event.target.value));
                    setAttendanceEmployeeId(event.target.value);
                    setAttendanceEmployeeCode('');
                    setAttendanceSearchEmployeeCode(selectedEmployee?.EmployeeCode || '');
                  }}>
                    <option value="">All employees</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.EmployeeId} value={employee.EmployeeId}>
                        {[employee.EmployeeCode, employee.FullName].filter(Boolean).join(' - ')}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={loadAttendanceReport}>Load</button>
                </div>
              </div>
              <div className="hr-mini-stats">
                <div><strong>{attendanceSummary.presentDays || 0}</strong><span>Present</span></div>
                <div><strong>{attendanceSummary.lateDays || 0}</strong><span>Late Days</span></div>
                <div><strong>{Number(attendanceSummary.overtimeHours || 0).toFixed(2)}h</strong><span>Overtime</span></div>
              </div>
              <div className="hr-list-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee Code</th>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Check-In</th>
                      <th>Check-Out</th>
                      <th>Late</th>
                      <th>OT</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="hr-empty-cell">No attendance rows for this range.</td>
                      </tr>
                    ) : attendance.map((row) => (
                      <tr key={row.AttendanceId}>
                        <td>{row.EmployeeCode || '-'}</td>
                        <td>{row.FullName}</td>
                        <td>{new Date(row.AttendanceDate).toLocaleDateString()}</td>
                        <td>{row.CheckInTime ? new Date(row.CheckInTime).toLocaleTimeString() : '-'}</td>
                        <td>{row.CheckOutTime ? new Date(row.CheckOutTime).toLocaleTimeString() : '-'}</td>
                        <td>{row.LateMinutes || 0} min</td>
                        <td>{row.OvertimeMinutes || 0} min</td>
                        <td>{row.AttendanceStatus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="hr-card">
              <div className="hr-card-header">
                <h2>Daily Attendance Entry</h2>
                <button type="button" onClick={() => quickAttendanceAction('check-in')}>Quick Check-In</button>
              </div>
              <form className="hr-form" onSubmit={saveAttendance}>
                <div className="hr-form-grid">
                  <label>
                    Employee
                    <select value={attendanceForm.employeeId} onChange={(event) => setAttendanceForm((current) => ({ ...current, employeeId: event.target.value }))}>
                      <option value="">Select employee</option>
                      {employeeOptions.map((employee) => (
                        <option key={employee.EmployeeId} value={employee.EmployeeId}>{employee.FullName}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Date
                    <input type="date" value={attendanceForm.attendanceDate} onChange={(event) => setAttendanceForm((current) => ({ ...current, attendanceDate: event.target.value }))} />
                  </label>
                  <label>
                    Check-In
                    <input type="time" value={attendanceForm.checkInTime} onChange={(event) => setAttendanceForm((current) => ({ ...current, checkInTime: event.target.value }))} />
                  </label>
                  <label>
                    Check-Out
                    <input type="time" value={attendanceForm.checkOutTime} onChange={(event) => setAttendanceForm((current) => ({ ...current, checkOutTime: event.target.value }))} />
                  </label>
                  <label>
                    Status
                    <select value={attendanceForm.attendanceStatus} onChange={(event) => setAttendanceForm((current) => ({ ...current, attendanceStatus: event.target.value }))}>
                      <option value="Present">Present</option>
                      <option value="Late">Late</option>
                      <option value="Absent">Absent</option>
                      <option value="Half Day">Half Day</option>
                    </select>
                  </label>
                  <label className="full-span">
                    Notes
                    <textarea rows="3" value={attendanceForm.notes} onChange={(event) => setAttendanceForm((current) => ({ ...current, notes: event.target.value }))} />
                  </label>
                </div>
                <div className="hr-form-actions">
                  <button type="submit">Save Attendance</button>
                  <button type="button" className="secondary" onClick={() => quickAttendanceAction('check-out')}>Quick Check-Out</button>
                </div>
              </form>
            </section>
          </section>
        )}

        {activeTab === 'leave' && (
          <section className="hr-panel hr-two-column wide-left">
            <section className="hr-card">
              <div className="hr-card-header">
                <h2>Leave Requests</h2>
                <div className="hr-header-actions" style={{ display: 'flex', gap: '1rem' }}>
                  <input
                    type="text"
                    placeholder="Search by Employee ID"
                    value={leaveSearchEmployeeId}
                    onChange={(e) => setLeaveSearchEmployeeId(e.target.value)}
                  />
                  <select value={leaveBalanceEmployeeId} onChange={(event) => {
                    setLeaveBalanceEmployeeId(event.target.value);
                    loadLeaves(event.target.value).catch((loadError) => showError(loadError.response?.data?.message || 'Failed to load balances.'));
                  }}>
                    <option value="">Select employee for balance</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.EmployeeId} value={employee.EmployeeId}>{employee.FullName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="hr-list-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Type</th>
                      <th>Dates</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveRequests.filter(req => !leaveSearchEmployeeId || String(req.EmployeeId).includes(leaveSearchEmployeeId)).length === 0 ? (
                      <tr>
                        <td colSpan="6" className="hr-empty-cell">No leave requests found.</td>
                      </tr>
                    ) : leaveRequests.filter(req => !leaveSearchEmployeeId || String(req.EmployeeId).includes(leaveSearchEmployeeId)).map((request) => (
                      <tr key={request.LeaveRequestId}>
                        <td>{request.FullName}</td>
                        <td>{request.LeaveTypeName}</td>
                        <td>{new Date(request.StartDate).toLocaleDateString()} - {new Date(request.EndDate).toLocaleDateString()}</td>
                        <td>{request.DaysRequested}</td>
                        <td>{request.RequestStatus}</td>
                        <td className="hr-row-actions">
                          {request.RequestStatus === 'Pending' && access?.permissions?.leave && access?.role !== 'Employee' ? (
                            <>
                              <button type="button" onClick={() => decideLeave(request.LeaveRequestId, 'Approved')}>Approve</button>
                              <button type="button" className="danger" onClick={() => decideLeave(request.LeaveRequestId, 'Rejected')}>Reject</button>
                            </>
                          ) : (
                            <span className="hr-muted">{request.ReviewedByEmail || '-'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="hr-subsection">
                <h3>Leave Balances</h3>
                <div className="hr-list-table compact">
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Allocated</th>
                        <th>Used</th>
                        <th>Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveBalances.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="hr-empty-cell">Select an employee to view balances.</td>
                        </tr>
                      ) : leaveBalances.map((balance) => (
                        <tr key={balance.LeaveBalanceId}>
                          <td>{balance.LeaveTypeName}</td>
                          <td>{balance.AllocatedDays}</td>
                          <td>{balance.UsedDays}</td>
                          <td>{balance.RemainingDays}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="hr-card">
              <div className="hr-card-header">
                <h2>Create Leave Request</h2>
              </div>
              <form className="hr-form" onSubmit={submitLeaveRequest}>
                <div className="hr-form-grid">
                  <label>
                    Employee
                    <select value={leaveForm.employeeId} onChange={(event) => setLeaveForm((current) => ({ ...current, employeeId: event.target.value }))}>
                      <option value="">Select employee</option>
                      {employeeOptions.map((employee) => (
                        <option key={employee.EmployeeId} value={employee.EmployeeId}>{employee.FullName}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Leave Type
                    <select value={leaveForm.leaveTypeId} onChange={(event) => setLeaveForm((current) => ({ ...current, leaveTypeId: event.target.value }))}>
                      <option value="">Select leave type</option>
                      {lookups.leaveTypes.map((leaveType) => (
                        <option key={leaveType.LeaveTypeId} value={leaveType.LeaveTypeId}>{leaveType.LeaveTypeName}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Start Date
                    <input type="date" value={leaveForm.startDate} onChange={(event) => setLeaveForm((current) => ({ ...current, startDate: event.target.value }))} />
                  </label>
                  <label>
                    End Date
                    <input type="date" value={leaveForm.endDate} onChange={(event) => setLeaveForm((current) => ({ ...current, endDate: event.target.value }))} />
                  </label>
                  <label className="full-span">
                    Reason
                    <textarea rows="4" value={leaveForm.reason} onChange={(event) => setLeaveForm((current) => ({ ...current, reason: event.target.value }))} />
                  </label>
                </div>
                <div className="hr-form-actions">
                  <button type="submit">Submit Leave Request</button>
                </div>
              </form>
            </section>
          </section>
        )}

        {activeTab === 'payroll' && (
          <section className="hr-panel">
            <div className="hr-toolbar">
              <div className="hr-inline-actions">
                <label>
                  Payroll Month
                  <input type="month" value={payrollMonth} onChange={(event) => {
                    setPayrollMonth(event.target.value);
                    setAdjustmentForm((current) => ({ ...current, payrollMonth: event.target.value }));
                  }} />
                </label>
                <button type="button" onClick={() => loadPayroll(payrollMonth)}>Refresh Preview</button>
                <button type="button" onClick={printPayrollSheet}>Print Payroll Sheet</button>
                <button type="button" className="primary" onClick={generatePayroll}>Generate Payroll</button>
              </div>
              <div className="hr-inline-actions">
                <strong>{payrollPreview.summary.employeeCount || 0}</strong>
                <span>Employees</span>
                <strong>{currency(payrollPreview.summary.totalNet || 0)}</strong>
                <span>Net payout</span>
              </div>
            </div>

            <div className="hr-two-column wide-left">
              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Monthly Payroll Preview</h2>
                </div>
                <div className="hr-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Basic</th>
                        <th>Allowance</th>
                        <th>Deduction</th>
                        <th>OT</th>
                        <th>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollPreview.lines?.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="hr-empty-cell">No active employees available for payroll.</td>
                        </tr>
                      ) : payrollPreview.lines.map((line) => (
                        <tr key={line.EmployeeId}>
                          <td>{line.FullName}</td>
                          <td>{currency(line.BasicSalary)}</td>
                          <td>{currency(line.AllowanceAmount)}</td>
                          <td>{currency(line.DeductionAmount)}</td>
                          <td>{currency(line.OvertimeAmount)} <small>({line.OvertimeHours}h)</small></td>
                          <td><strong>{currency(line.NetSalary)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Adjustments & Runs</h2>
                </div>
                <form className="hr-form compact" onSubmit={addPayrollAdjustment}>
                  <div className="hr-form-grid">
                    <label>
                      Employee
                      <select value={adjustmentForm.employeeId} onChange={(event) => setAdjustmentForm((current) => ({ ...current, employeeId: event.target.value }))}>
                        <option value="">Select employee</option>
                        {employeeOptions.map((employee) => (
                          <option key={employee.EmployeeId} value={employee.EmployeeId}>{employee.FullName}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Type
                      <select value={adjustmentForm.adjustmentType} onChange={(event) => setAdjustmentForm((current) => ({ ...current, adjustmentType: event.target.value }))}>
                        <option value="Allowance">Allowance</option>
                        <option value="Bonus">Bonus</option>
                        <option value="Deduction">Deduction</option>
                        <option value="Advance">Advance</option>
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" step="0.01" value={adjustmentForm.amount} onChange={(event) => setAdjustmentForm((current) => ({ ...current, amount: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Notes
                      <input value={adjustmentForm.notes} onChange={(event) => setAdjustmentForm((current) => ({ ...current, notes: event.target.value }))} />
                    </label>
                  </div>
                  <div className="hr-form-actions">
                    <button type="submit">Add Adjustment</button>
                  </div>
                </form>

                <div className="hr-subsection">
                  <h3>Current Month Adjustments</h3>
                  <ul className="hr-compact-list">
                    {payrollAdjustments.length === 0 ? (
                      <li>No adjustments added for this month.</li>
                    ) : payrollAdjustments.map((adjustment) => (
                      <li key={adjustment.PayrollAdjustmentId}>
                        <strong>{adjustment.FullName}</strong> {adjustment.AdjustmentType} {currency(adjustment.Amount)}
                        {adjustment.Notes ? ` | ${adjustment.Notes}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="hr-subsection">
                  <h3>Payroll History</h3>
                  <ul className="hr-compact-list">
                    {payrollRuns.length === 0 ? (
                      <li>No payroll runs generated yet.</li>
                    ) : payrollRuns.map((run) => (
                      <li key={run.PayrollRunId} className="hr-payroll-run-item">
                        <div className="hr-payroll-run-copy">
                          <strong>{run.PayrollMonth}</strong> | {run.EmployeeCount || 0} employees | {currency(run.TotalNetSalary || 0)}
                          <div className="hr-payroll-run-meta">
                            <span className="hr-pill hr-pill-balanced">{run.PaymentStatus || 'Unpaid'}</span>
                            <span>Paid {currency(run.TotalPaidAmount || 0)}</span>
                            <span>Due {currency(run.BalanceAmount || 0)}</span>
                          </div>
                        </div>
                        {Number(run.BalanceAmount || 0) > 0 ? (
                          <button type="button" onClick={() => openPayrollPaymentDialog(run)}>Pay Salary</button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>
          </section>
        )}

        {activeTab === 'workshop' && access?.role !== 'Employee' && (
          <section className="hr-panel">
            <section className="hr-card">
              <div className="hr-card-header">
                <div>
                  <h2>Workshop Board</h2>
                  <span>Task queue, technician load, and performance by month</span>
                </div>
                <div className="hr-inline-actions">
                  <input type="month" value={workshopMonth} onChange={(event) => setWorkshopMonth(event.target.value)} />
                  <button type="button" onClick={() => loadWorkshop(workshopMonth)}>Refresh Board</button>
                </div>
              </div>

              <div className="hr-stat-grid">
                <article className="hr-stat-card">
                  <span>Queued Tasks</span>
                  <strong>{workshopBoard.summary.totalQueuedTasks || 0}</strong>
                  <small>Assigned labor rows still open</small>
                </article>
                <article className="hr-stat-card">
                  <span>Active Technicians</span>
                  <strong>{workshopBoard.summary.activeTechnicians || 0}</strong>
                  <small>With queue items or running jobs</small>
                </article>
                <article className="hr-stat-card">
                  <span>Overloaded</span>
                  <strong>{workshopBoard.summary.overloadedTechnicians || 0}</strong>
                  <small>Beyond target load thresholds</small>
                </article>
                <article className="hr-stat-card">
                  <span>Logged This Month</span>
                  <strong>{hoursLabel(workshopBoard.summary.totalLoggedHours || 0)}</strong>
                  <small>Standard: {hoursLabel(workshopBoard.summary.totalStandardHours || 0)}</small>
                </article>
              </div>
            </section>

            <div className="hr-two-column wide-left">
              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Technician Workload Board</h2>
                  <span>Open jobs, remaining hours, efficiency, and score</span>
                </div>
                <div className="hr-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Technician</th>
                        <th>Open Tasks</th>
                        <th>Open Jobs</th>
                        <th>Remaining</th>
                        <th>Efficiency</th>
                        <th>Score</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workshopBoard.employees.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="hr-empty-cell">No workshop assignments found.</td>
                        </tr>
                      ) : workshopBoard.employees.map((employee) => (
                        <tr
                          key={employee.EmployeeId}
                          className={`hr-workload-row ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}
                        >
                          <td>
                            <div className="hr-identity stacked">
                              <strong>{employee.FullName}</strong>
                              <small>{employee.DesignationName || employee.RoleName || employee.DepartmentName || 'Workshop'}</small>
                            </div>
                          </td>
                          <td>
                            <span className={`hr-task-count ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}>
                              {employee.OpenTaskCount || 0}
                            </span>
                          </td>
                          <td>{employee.OpenJobCount || 0}</td>
                          <td>{hoursLabel(employee.RemainingHours || 0)}</td>
                          <td>{percentLabel(employee.EfficiencyPercent || 0)}</td>
                          <td>{percentLabel(employee.ProductivityScorePercent || 0)}</td>
                          <td>
                            <span className={`hr-pill hr-pill-${String(employee.WorkloadStatus || '').toLowerCase()}`}>
                              {employee.WorkloadStatus || 'Balanced'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Best Mechanic Leaderboard</h2>
                  <span>{workshopBoard.month || workshopMonth}</span>
                </div>
                <div className="hr-list-table compact">
                  <table>
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Technician</th>
                        <th>Score</th>
                        <th>Efficiency</th>
                        <th>Jobs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workshopBoard.leaderboard.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="hr-empty-cell">No monthly productivity data available.</td>
                        </tr>
                      ) : workshopBoard.leaderboard.map((employee, index) => (
                        <tr key={employee.EmployeeId}>
                          <td>#{index + 1}</td>
                          <td>{employee.FullName}</td>
                          <td>{percentLabel(employee.ProductivityScorePercent || 0)}</td>
                          <td>{percentLabel(employee.EfficiencyPercent || 0)}</td>
                          <td>{employee.ClosedJobs || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <section className="hr-card">
              <div className="hr-card-header">
                <div>
                  <h2>Employee-wise Task Queue</h2>
                  <span>Assigned labor items from open job cards</span>
                </div>
                <div className="hr-queue-header-summary">
                  <strong>{queuePagination.total}</strong>
                  <span>matching employees</span>
                </div>
              </div>
              <div className="hr-queue-toolbar">
                <input
                  className="hr-queue-search"
                  type="search"
                  value={queueSearch}
                  onChange={(event) => setQueueSearch(event.target.value)}
                  placeholder="Search employee, job card, plate, labor, client"
                />
                <div className="hr-queue-controls">
                  <label>
                    Sort
                    <select value={queueSort} onChange={(event) => setQueueSort(event.target.value)}>
                      <option value="priority">Running, tasks, remaining</option>
                      <option value="tasks">Open tasks high to low</option>
                      <option value="remaining">Remaining hours high to low</option>
                      <option value="score">Score high to low</option>
                      <option value="name">Name A-Z</option>
                    </select>
                  </label>
                  <label>
                    Per Page
                    <select value={queuePageSize} onChange={(event) => setQueuePageSize(Number(event.target.value))}>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="hr-queue-filter-chips" role="tablist" aria-label="Queue filters">
                {[
                  ['active', 'Active Queue'],
                  ['running', 'Running Now'],
                  ['open', 'Has Open Tasks'],
                  ['idle', 'Idle'],
                  ['overloaded', 'Overloaded'],
                  ['all', 'All Employees'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={queueFilter === value ? 'active' : ''}
                    onClick={() => setQueueFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {workshopBoard.employees.length === 0 ? (
                <div className="hr-empty-panel">No technician queue is ready yet.</div>
              ) : queuePagination.total === 0 ? (
                <div className="hr-empty-panel">No employees match this queue filter.</div>
              ) : (
                <>
                  <div className="hr-queue-results">
                    <span>
                      Showing {queuePagination.startIndex + 1}-{queuePagination.endIndex} of {queuePagination.total} employees
                    </span>
                    <span>Compact view keeps the queue easier to scan for large teams.</span>
                  </div>

                  <div className="hr-list-table hr-queue-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Technician</th>
                          <th>State</th>
                          <th>Open Tasks</th>
                          <th>Open Jobs</th>
                          <th>Remaining</th>
                          <th>Score</th>
                          <th>Queue</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {visibleQueueEmployees.map((employee) => (
                          <React.Fragment key={employee.EmployeeId}>
                            <tr className={`hr-workload-row ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}>
                              <td>
                                <div className="hr-identity stacked">
                                  <strong>{employee.FullName}</strong>
                                  <small>{employee.DesignationName || employee.RoleName || employee.DepartmentName || 'Workshop'}</small>
                                </div>
                              </td>
                              <td>
                                <div className="hr-queue-state-stack">
                                  <strong className={`hr-queue-state ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}>
                                    {(employee.ActiveTaskCount || 0) > 0 ? 'Running' : 'Idle'}
                                  </strong>
                                  <span className={`hr-pill hr-pill-${String(employee.WorkloadStatus || '').toLowerCase()}`}>
                                    {employee.WorkloadStatus || 'Balanced'}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span className={`hr-task-count ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}>
                                  {employee.OpenTaskCount || 0}
                                </span>
                              </td>
                              <td>{employee.OpenJobCount || 0}</td>
                              <td>{hoursLabel(employee.RemainingHours || 0)}</td>
                              <td>{percentLabel(employee.ProductivityScorePercent || 0)}</td>
                              <td>{employee.QueueItems?.length || 0}</td>
                              <td>
                                <button
                                  type="button"
                                  className="hr-queue-expand-button"
                                  onClick={() => toggleQueueEmployee(employee.EmployeeId)}
                                >
                                  {expandedQueueEmployees[employee.EmployeeId] ? 'Collapse' : 'Expand'}
                                </button>
                              </td>
                            </tr>
                            {expandedQueueEmployees[employee.EmployeeId] && (
                              <tr className="hr-queue-detail-row">
                                <td colSpan="8">
                                  <div className="hr-queue-detail-shell">
                                    <div className="hr-mini-stats">
                                      <div>
                                        <strong>{employee.ActiveTaskCount || 0}</strong>
                                        <span>Running</span>
                                      </div>
                                      <div>
                                        <strong>{employee.OpenTaskCount || 0}</strong>
                                        <span>Open Tasks</span>
                                      </div>
                                      <div>
                                        <strong>{hoursLabel(employee.RemainingHours || 0)}</strong>
                                        <span>Remaining</span>
                                      </div>
                                      <div>
                                        <strong>{percentLabel(employee.EfficiencyPercent || 0)}</strong>
                                        <span>Efficiency</span>
                                      </div>
                                    </div>

                                    <div className="hr-task-list">
                                      {employee.QueueItems?.length ? employee.QueueItems.map((task) => (
                                        <div key={task.JobCardLaborId} className="hr-task-item">
                                          <div className="hr-task-meta">
                                            <strong>{task.JobNumber}</strong>
                                            <span>{task.JobStatus}</span>
                                          </div>
                                          <h4>{task.LaborName}</h4>
                                          <p>{task.ClientName || 'Walk-in customer'} {task.VehicleLabel ? `| ${task.VehicleLabel}` : ''}</p>
                                          <div className="hr-task-stats">
                                            <span>Actual {hoursLabel(task.LoggedHours || 0)}</span>
                                            <span>Standard {hoursLabel(task.StandardHours || 0)}</span>
                                            <span>Remain {hoursLabel(task.RemainingHours || 0)}</span>
                                            <span>Efficiency {percentLabel(task.ProgressPercent || 0)}</span>
                                          </div>
                                          <div className="hr-queue-detail-actions">
                                            {task.IsRunning && (
                                              <div className="hr-task-live">Running now - {task.RunningMinutes || 0} min active</div>
                                            )}
                                            <button
                                              type="button"
                                              className="hr-inline-link"
                                              onClick={() => navigate(`/jobcards/${task.JobCardId}/edit`)}
                                            >
                                              Open Job Card
                                            </button>
                                          </div>
                                        </div>
                                      )) : (
                                        <div className="hr-empty-panel compact">No open tasks assigned.</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="hr-queue-pagination">
                    <span>
                      Page {queuePagination.currentPage} of {queuePagination.pageCount}
                    </span>
                    <div className="hr-queue-pagination-actions">
                      <button
                        type="button"
                        onClick={() => setQueuePage((current) => Math.max(1, current - 1))}
                        disabled={queuePagination.currentPage <= 1}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setQueuePage((current) => Math.min(queuePagination.pageCount, current + 1))}
                        disabled={queuePagination.currentPage >= queuePagination.pageCount}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}

              {false && (
              <div className="hr-queue-grid">
                {workshopBoard.employees.length === 0 ? (
                  <div className="hr-empty-panel">No technician queue is ready yet.</div>
                ) : workshopBoard.employees.map((employee) => (
                  <article
                    key={employee.EmployeeId}
                    className={`hr-queue-card ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}
                  >
                    <div className="hr-queue-head">
                      <div>
                        <h3>{employee.FullName}</h3>
                        <p>{employee.DesignationName || employee.RoleName || 'Workshop staff'}</p>
                      </div>
                      <span className={`hr-pill hr-pill-${String(employee.WorkloadStatus || '').toLowerCase()}`}>
                        {employee.WorkloadStatus || 'Balanced'}
                      </span>
                    </div>

                    <div className="hr-mini-stats">
                      <div>
                        <strong className={`hr-task-count ${(employee.ActiveTaskCount || 0) > 0 ? 'is-running' : 'is-idle'}`}>
                          {employee.OpenTaskCount || 0}
                        </strong>
                        <span>Tasks</span>
                      </div>
                      <div>
                        <strong>{hoursLabel(employee.RemainingHours || 0)}</strong>
                        <span>Remaining</span>
                      </div>
                      <div>
                        <strong>{percentLabel(employee.ProductivityScorePercent || 0)}</strong>
                        <span>Score</span>
                      </div>
                    </div>

                    <div className="hr-task-list">
                      {employee.QueueItems?.length ? employee.QueueItems.map((task) => (
                        <div key={task.JobCardLaborId} className="hr-task-item">
                          <div className="hr-task-meta">
                            <strong>{task.JobNumber}</strong>
                            <span>{task.JobStatus}</span>
                          </div>
                          <h4>{task.LaborName}</h4>
                          <p>{task.ClientName || 'Walk-in customer'} {task.VehicleLabel ? `| ${task.VehicleLabel}` : ''}</p>
                          <div className="hr-task-stats">
                            <span>Actual {hoursLabel(task.LoggedHours || 0)}</span>
                            <span>Standard {hoursLabel(task.StandardHours || 0)}</span>
                            <span>Remain {hoursLabel(task.RemainingHours || 0)}</span>
                            <span>Efficiency {percentLabel(task.ProgressPercent || 0)}</span>
                          </div>
                          {task.IsRunning && (
                            <div className="hr-task-live">Running now • {task.RunningMinutes || 0} min active</div>
                          )}
                        </div>
                      )) : (
                        <div className="hr-empty-panel compact">No open tasks assigned.</div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
              )}
            </section>

          </section>
        )}

        {activeTab === 'settings' && access?.role !== 'Employee' && (
          <section className="hr-panel">
            <div className="hr-two-column wide-left">
              <section className="hr-card">
                <div className="hr-card-header">
                  <div>
                    <h2>Leave Type Settings</h2>
                    <span>Set yearly allocation, paid status, and active leave types</span>
                  </div>
                  {leaveTypeForm.leaveTypeId ? <button type="button" onClick={resetLeaveTypeForm}>New Leave Type</button> : null}
                </div>

                <div className="hr-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Code</th>
                        <th>Allocated</th>
                        <th>Paid</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveTypeSettings.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="hr-empty-cell">No leave types found.</td>
                        </tr>
                      ) : leaveTypeSettings.map((leaveType) => (
                        <tr key={leaveType.LeaveTypeId}>
                          <td>{leaveType.LeaveTypeName}</td>
                          <td>{leaveType.LeaveCode}</td>
                          <td>{leaveType.AnnualAllocation}</td>
                          <td>{leaveType.IsPaid ? 'Paid' : 'Unpaid'}</td>
                          <td>{leaveType.IsActive ? 'Active' : 'Inactive'}</td>
                          <td className="hr-row-actions">
                            <button type="button" onClick={() => startEditLeaveType(leaveType)}>Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>{leaveTypeForm.leaveTypeId ? 'Edit Leave Type' : 'Add Leave Type'}</h2>
                </div>
                <form className="hr-form compact" onSubmit={saveLeaveTypeSettings}>
                  <div className="hr-form-grid">
                    <label>
                      Leave Type Name
                      <input value={leaveTypeForm.leaveTypeName} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, leaveTypeName: event.target.value }))} />
                    </label>
                    <label>
                      Leave Code
                      <input value={leaveTypeForm.leaveCode} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, leaveCode: event.target.value.toUpperCase() }))} />
                    </label>
                    <label>
                      Annual Allocation
                      <input type="number" step="0.01" value={leaveTypeForm.annualAllocation} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, annualAllocation: event.target.value }))} />
                    </label>
                    <label className="hr-inline-check">
                      <input type="checkbox" checked={leaveTypeForm.isPaid} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, isPaid: event.target.checked }))} />
                      Paid Leave
                    </label>
                    <label className="hr-inline-check">
                      <input type="checkbox" checked={leaveTypeForm.isActive} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, isActive: event.target.checked }))} />
                      Active Leave Type
                    </label>
                  </div>
                  <div className="hr-form-actions">
                    <button type="submit">{leaveTypeForm.leaveTypeId ? 'Update Leave Type' : 'Save Leave Type'}</button>
                    {leaveTypeForm.leaveTypeId ? <button type="button" className="secondary" onClick={resetLeaveTypeForm}>Cancel</button> : null}
                  </div>
                </form>
              </section>
            </div>

            <section className="hr-card">
              <div className="hr-card-header">
                <div>
                  <h2>Year Calendar Settings</h2>
                  <span>Mark weekend rules and holidays for payroll working-day calculation</span>
                </div>
                <div className="hr-inline-actions">
                  <label className="hr-calendar-year-input">
                    Year
                    <input type="number" min="2020" max="2100" value={settingsYear} onChange={(event) => setSettingsYear(event.target.value)} />
                  </label>
                  <button type="button" onClick={() => loadHrSettings(settingsYear)}>Load Year</button>
                </div>
              </div>

              <div className="hr-two-column wide-left">
                <div className="hr-settings-stack">
                  <section className="hr-subcard">
                    <div className="hr-card-header">
                      <h3>Weekend Days</h3>
                      <button type="button" onClick={saveWeekendSettings}>Save Weekends</button>
                    </div>
                    <div className="hr-weekend-chips">
                      {WEEKDAY_OPTIONS.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          className={calendarSettings.weekendDays.includes(day.value) ? 'active' : ''}
                          onClick={() => toggleWeekendDay(day.value)}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="hr-subcard">
                    <div className="hr-card-header">
                      <h3>{holidayForm.holidayId ? 'Edit Holiday' : 'Add Holiday'}</h3>
                      {holidayForm.holidayId ? <button type="button" className="secondary" onClick={resetHolidayForm}>New</button> : null}
                    </div>
                    <form className="hr-form compact" onSubmit={saveHoliday}>
                      <div className="hr-form-grid">
                        <label>
                          Holiday Date
                          <input type="date" value={holidayForm.holidayDate} onChange={(event) => setHolidayForm((current) => ({ ...current, holidayDate: event.target.value }))} />
                        </label>
                        <label>
                          Holiday Name
                          <input value={holidayForm.holidayName} onChange={(event) => setHolidayForm((current) => ({ ...current, holidayName: event.target.value }))} />
                        </label>
                        <label className="full-span">
                          Notes
                          <input value={holidayForm.notes} onChange={(event) => setHolidayForm((current) => ({ ...current, notes: event.target.value }))} />
                        </label>
                      </div>
                      <div className="hr-form-actions">
                        <button type="submit">{holidayForm.holidayId ? 'Update Holiday' : 'Save Holiday'}</button>
                        {holidayForm.holidayId ? <button type="button" className="secondary" onClick={deleteHoliday}>Delete</button> : null}
                      </div>
                    </form>

                    <div className="hr-subsection">
                      <h3>Saved Holidays</h3>
                      <ul className="hr-compact-list">
                        {calendarSettings.holidays.length === 0 ? (
                          <li>No holidays saved for {calendarSettings.year}.</li>
                        ) : calendarSettings.holidays.map((holiday) => (
                          <li key={holiday.HolidayId}>
                            <button type="button" className="hr-inline-link" onClick={() => selectCalendarDate(holiday.HolidayDate)}>
                              {holiday.HolidayDate} | {holiday.HolidayName}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                </div>

                <section className="hr-subcard">
                  <div className="hr-card-header">
                    <h3>{calendarSettings.year} Calendar</h3>
                    <span>Click a date to add or edit a holiday. Red dates are weekends or holidays.</span>
                  </div>
                  <div className="hr-year-calendar">
                    {calendarMonths.map((month) => (
                      <article key={month.label} className="hr-month-card">
                        <h4>{month.label}</h4>
                        <div className="hr-month-weekdays">
                          {WEEKDAY_OPTIONS.map((day) => (
                            <span key={`${month.label}-${day.value}`}>{day.label}</span>
                          ))}
                        </div>
                        <div className="hr-month-grid">
                          {month.cells.map((cell) => {
                            if (cell.isBlank) {
                              return <span key={cell.key} className="hr-calendar-day blank" aria-hidden="true" />;
                            }

                            const isWeekend = calendarSettings.weekendDays.includes(cell.weekday);
                            const isHoliday = Boolean(cell.holiday);
                            const isSelected = holidayForm.holidayDate === cell.dateKey;

                            return (
                              <button
                                key={cell.key}
                                type="button"
                                className={`hr-calendar-day${isWeekend ? ' weekend' : ''}${isHoliday ? ' holiday' : ''}${isSelected ? ' selected' : ''}`}
                                onClick={() => selectCalendarDate(cell.dateKey)}
                                title={isHoliday ? cell.holiday.HolidayName : cell.dateKey}
                              >
                                <strong>{cell.day}</strong>
                                {isHoliday ? <small>{cell.holiday.HolidayName}</small> : null}
                              </button>
                            );
                          })}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </div>
            </section>

            {access?.role === 'Admin' && (
              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Productivity Rules</h2>
                  <span>Set how workload and leaderboard scores are calculated</span>
                </div>
                <form className="hr-form compact" onSubmit={saveProductivitySettings}>
                  <div className="hr-form-grid">
                    <label>
                      Efficiency Weight
                      <input type="number" step="0.01" value={productivitySettingsForm.LeaderboardWeightEfficiency} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, LeaderboardWeightEfficiency: event.target.value }))} />
                    </label>
                    <label>
                      Revenue Weight
                      <input type="number" step="0.01" value={productivitySettingsForm.LeaderboardWeightRevenue} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, LeaderboardWeightRevenue: event.target.value }))} />
                    </label>
                    <label>
                      Hours Weight
                      <input type="number" step="0.01" value={productivitySettingsForm.LeaderboardWeightHours} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, LeaderboardWeightHours: event.target.value }))} />
                    </label>
                    <label>
                      Closed Jobs Weight
                      <input type="number" step="0.01" value={productivitySettingsForm.LeaderboardWeightClosedJobs} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, LeaderboardWeightClosedJobs: event.target.value }))} />
                    </label>
                    <label>
                      Target Efficiency %
                      <input type="number" step="0.01" value={productivitySettingsForm.TargetEfficiencyPercent} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, TargetEfficiencyPercent: event.target.value }))} />
                    </label>
                    <label>
                      Target Billable Hours / Day
                      <input type="number" step="0.01" value={productivitySettingsForm.TargetBillableHoursPerDay} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, TargetBillableHoursPerDay: event.target.value }))} />
                    </label>
                    <label>
                      Overload Hours Threshold
                      <input type="number" step="0.01" value={productivitySettingsForm.OverloadHoursThreshold} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, OverloadHoursThreshold: event.target.value }))} />
                    </label>
                    <label>
                      High Task Threshold
                      <input type="number" step="1" value={productivitySettingsForm.HighTaskCountThreshold} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, HighTaskCountThreshold: event.target.value }))} />
                    </label>
                    <label>
                      Low Task Threshold
                      <input type="number" step="1" value={productivitySettingsForm.LowTaskCountThreshold} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, LowTaskCountThreshold: event.target.value }))} />
                    </label>
                    <label>
                      Shift Start Time
                      <input type="time" value={productivitySettingsForm.ShiftStartTime} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, ShiftStartTime: event.target.value }))} />
                    </label>
                    <label>
                      Shift End Time
                      <input type="time" value={productivitySettingsForm.ShiftEndTime} onChange={(event) => setProductivitySettingsForm((current) => ({ ...current, ShiftEndTime: event.target.value }))} />
                    </label>
                  </div>
                  <div className="hr-form-actions">
                    <button type="submit">Save Productivity Rules</button>
                  </div>
                </form>
              </section>
            )}

            {access?.role === 'Admin' && (
              <section className="hr-card">
                <div className="hr-card-header">
                  <h2>Roles & Permissions</h2>
                  <span>Admin can manage role mappings</span>
                </div>
                <div className="hr-two-column">
                  <form className="hr-form compact" onSubmit={saveRoleMapping}>
                    <div className="hr-form-grid">
                      <label>
                        User Email
                        <input type="email" value={roleForm.userEmail} onChange={(event) => setRoleForm((current) => ({ ...current, userEmail: event.target.value }))} />
                      </label>
                      <label>
                        App Role
                        <select value={roleForm.appRoleName} onChange={(event) => setRoleForm((current) => ({ ...current, appRoleName: event.target.value }))}>
                          <option value="Admin">Admin</option>
                          <option value="HR">HR</option>
                          <option value="Employee">Employee</option>
                        </select>
                      </label>
                      <label className="full-span">
                        Linked Employee
                        <select value={roleForm.employeeId} onChange={(event) => setRoleForm((current) => ({ ...current, employeeId: event.target.value }))}>
                          <option value="">Optional employee link</option>
                          {employees.map((employee) => (
                            <option key={employee.EmployeeId} value={employee.EmployeeId}>{employee.FullName}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="hr-form-actions">
                      <button type="submit">Save Role Mapping</button>
                    </div>
                  </form>

                  <div className="hr-list-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Employee</th>
                          <th>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleMappings.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="hr-empty-cell">No role mappings yet.</td>
                          </tr>
                        ) : roleMappings.map((mapping) => (
                          <tr key={mapping.HRUserRoleId}>
                            <td>{mapping.UserEmail}</td>
                            <td>{mapping.AppRoleName}</td>
                            <td>{mapping.FullName || '-'}</td>
                            <td>{mapping.UpdatedDate ? new Date(mapping.UpdatedDate).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}
          </section>
        )}

        {activeTab === 'reports' && (
          <section className="hr-panel">
            <section className="hr-card hr-report-showcase">
              <div className="hr-card-header">
                <div>
                  <h2>Report Pages</h2>
                  <span>Open beautiful live report pages instead of downloading CSV files</span>
                </div>
                <div className="hr-inline-actions">
                  <label>
                    Report Month
                    <input type="month" value={reportMonth} onChange={(event) => setReportMonth(event.target.value)} />
                  </label>
                  {activeReportMeta ? (
                    <button type="button" onClick={() => openReportWorkspace(activeReportMeta.id)}>
                      {reportLoading ? 'Loading...' : 'Refresh Open Report'}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="hr-report-launcher">
                {HR_REPORT_CATALOG.map((report) => (
                  <button
                    key={report.id}
                    type="button"
                    className={`hr-report-launch-card hr-report-tone-${report.id}${reportWorkspace.reportType === report.id ? ' active' : ''}`}
                    onClick={() => openReportWorkspace(report.id)}
                  >
                    <span className="hr-report-launch-eyebrow">{report.eyebrow}</span>
                    <strong>{report.title}</strong>
                    <p>{report.description}</p>
                    <small>Open report page for {monthLabel(reportMonth)}</small>
                  </button>
                ))}
              </div>
            </section>

            {activeReportMeta ? (
              <section className={`hr-card hr-report-page hr-report-page-${activeReportMeta.id}`}>
                <div className="hr-card-header">
                  <div>
                    <span className="hr-report-page-kicker">{activeReportMeta.eyebrow}</span>
                    <h2>{activeReportMeta.title}</h2>
                    <span>{activeReportMeta.description}</span>
                  </div>
                  <div className="hr-inline-actions">
                    <strong>{monthLabel(reportMonth)}</strong>
                    <span>{reportWorkspace.loadedAt ? `Updated ${formatHumanDateTime(reportWorkspace.loadedAt)}` : 'Waiting for report data'}</span>
                  </div>
                </div>

                <div className="hr-report-summary-grid">
                  {reportSummaryCards.map((card) => (
                    <article key={card.label} className={`hr-report-summary-card hr-report-summary-${card.tone}`}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </article>
                  ))}
                </div>

                <div className="hr-report-table-shell">
                  {reportLoading ? (
                    <div className="hr-loading compact">Loading report page...</div>
                  ) : reportWorkspace.rows.length === 0 ? (
                    <div className="hr-empty-panel">No rows found for this report.</div>
                  ) : (
                    <div className="hr-list-table hr-report-list-table">
                      <table>
                        <thead>
                          <tr>
                            {reportColumns.map((column) => (
                              <th key={column.key}>{column.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportWorkspace.rows.map((row, index) => (
                            <tr key={`${reportWorkspace.reportType}-${index}`}>
                              {reportColumns.map((column) => (
                                <td key={column.key}>
                                  {column.render ? column.render(row) : (row[column.key] ?? '-')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            <section className="hr-card">
              <div className="hr-card-header">
                <h2>Workshop Integration</h2>
                <span>Monthly productivity and efficiency</span>
              </div>
              <div className="hr-list-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Jobs</th>
                      <th>Actual</th>
                      <th>Standard</th>
                      <th>Efficiency</th>
                      <th>Score</th>
                      <th>Revenue</th>
                      <th>Rev/Hr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productivity.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="hr-empty-cell">No workshop productivity found.</td>
                      </tr>
                    ) : productivity.map((item) => (
                      <tr key={item.EmployeeId}>
                        <td>{item.FullName}</td>
                        <td>{item.JobsWorked || 0}</td>
                        <td>{hoursLabel(item.LoggedHours || 0)}</td>
                        <td>{hoursLabel(item.StandardHours || 0)}</td>
                        <td>{percentLabel(item.EfficiencyPercent || 0)}</td>
                        <td>{percentLabel(item.ProductivityScorePercent || 0)}</td>
                        <td>{currency(item.LaborRevenue || 0)}</td>
                        <td>{currency(item.RevenuePerHour || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

          </section>
        )}

        {payrollPaymentDialog.open && (
          <div className="hr-dialog-backdrop" onClick={closePayrollPaymentDialog}>
            <div className="hr-dialog hr-payroll-dialog" onClick={(event) => event.stopPropagation()}>
              <div className="hr-card-header">
                <div>
                  <h2>Pay Salary</h2>
                  <span>
                    {payrollPaymentDialog.run?.PayrollMonth || payrollMonth}
                    {payrollPaymentDialog.run ? ` | ${payrollPaymentDialog.run.EmployeeCount || payrollPaymentDialog.employees.length} employees` : ''}
                  </span>
                </div>
                <button type="button" className="hr-dialog-close" onClick={closePayrollPaymentDialog}>Close</button>
              </div>

              {payrollPaymentDialog.loading ? (
                <div className="hr-loading compact">Loading payroll payment workspace...</div>
              ) : (
                <>
                  <div className="hr-mini-stats">
                    <div>
                      <strong>{currency(payrollPaymentTotals.totalDue)}</strong>
                      <span>Total Due</span>
                    </div>
                    <div>
                      <strong>{currency(payrollPaymentTotals.totalPaying)}</strong>
                      <span>This Payment</span>
                    </div>
                    <div>
                      <strong>{currency(payrollPaymentTotals.remaining)}</strong>
                      <span>Remaining After</span>
                    </div>
                  </div>

                  <div className="hr-form-grid hr-payroll-payment-grid">
                    <label>
                      Payment Date
                      <input
                        type="date"
                        value={payrollPaymentDialog.paymentDate}
                        onChange={(event) => setPayrollPaymentDialog((current) => ({ ...current, paymentDate: event.target.value }))}
                      />
                    </label>
                    <label>
                      Payment Account
                      <select
                        value={payrollPaymentDialog.paymentAccountCode}
                        onChange={(event) => setPayrollPaymentDialog((current) => ({ ...current, paymentAccountCode: event.target.value }))}
                      >
                        {payrollPaymentDialog.accounts.map((account) => (
                          <option key={account.AccountId} value={account.AccountCode}>
                            {account.AccountCode} - {account.AccountName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="full-span">
                      Notes
                      <input
                        value={payrollPaymentDialog.notes}
                        onChange={(event) => setPayrollPaymentDialog((current) => ({ ...current, notes: event.target.value }))}
                        placeholder="Optional salary payment note"
                      />
                    </label>
                  </div>

                  <div className="hr-list-table hr-payroll-payment-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Net Salary</th>
                          <th>Already Paid</th>
                          <th>Due</th>
                          <th>Pay Now</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollPaymentDialog.employees.map((employee) => {
                          const entry = payrollPaymentDialog.entries.find((item) => item.payrollRunEmployeeId === employee.PayrollRunEmployeeId);
                          return (
                            <tr key={employee.PayrollRunEmployeeId}>
                              <td>{employee.FullName}</td>
                              <td>{currency(employee.NetSalary || 0)}</td>
                              <td>{currency(employee.PaidAmount || 0)}</td>
                              <td>{currency(employee.BalanceAmount || 0)}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  max={Number(employee.BalanceAmount || 0)}
                                  value={entry?.amount ?? 0}
                                  onChange={(event) => updatePayrollPaymentEntry(employee.PayrollRunEmployeeId, event.target.value)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="hr-subsection">
                    <h3>Payment History</h3>
                    <ul className="hr-compact-list">
                      {payrollPaymentDialog.payments.length === 0 ? (
                        <li>No salary payments posted for this payroll run yet.</li>
                      ) : payrollPaymentDialog.payments.map((payment) => (
                        <li key={payment.PayrollPaymentId}>
                          <strong>{payment.PaymentDate ? new Date(payment.PaymentDate).toLocaleDateString() : '-'}</strong>
                          {' | '}
                          {currency(payment.PaymentAmount || 0)}
                          {' | '}
                          {payment.PaymentAccountName || payment.PaymentAccountCode}
                          {' | '}
                          {payment.PaymentStatus}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="hr-form-actions">
                    <button type="button" className="secondary" onClick={closePayrollPaymentDialog}>Cancel</button>
                    <button type="button" onClick={submitPayrollPayment} disabled={payrollPaymentDialog.loading}>
                      {payrollPaymentDialog.loading ? 'Posting Payment...' : 'Post Salary Payment'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default HrModule;
